<template>
  <app-content :title="'业务转凭证'">
    <div class="business-voucher-page">
      <div class="evidence-query-bar">
        <div class="evidence-query-title">
          <i class="h-icon-search"></i>
          证据链只读查询
        </div>
        <div class="evidence-query-form">
          <input
            v-model.trim="evidenceQuery.voucherId"
            type="text"
            class="evidence-query-input"
            placeholder="voucherId"
          />
          <span class="evidence-query-separator">或</span>
          <input
            v-model.trim="evidenceQuery.traceId"
            type="text"
            class="evidence-query-input"
            placeholder="traceId"
          />
          <button class="btn btn-primary btn-sm" @click="openEvidenceChainByQuery">
            <i class="h-icon-eye"></i>
            查看证据链
          </button>
          <button class="btn btn-default btn-sm" @click="fillEvidenceQueryFromCurrent">
            <i class="h-icon-refresh"></i>
            使用当前样本
          </button>
          <button class="btn btn-default btn-sm danger" @click="openFailedSamplesQuery">
            <i class="h-icon-warning"></i>
            查询失败样本
          </button>
        </div>
        <div class="failed-query-form">
          <input
            v-model="failedSampleQuery.startDate"
            type="date"
            class="evidence-query-input"
            placeholder="开始日期"
          />
          <input
            v-model="failedSampleQuery.endDate"
            type="date"
            class="evidence-query-input"
            placeholder="结束日期"
          />
          <input
            v-model.trim="failedSampleQuery.eventType"
            type="text"
            class="evidence-query-input"
            placeholder="eventType（可选）"
          />
          <input
            v-model.trim="failedSampleQuery.errorCode"
            type="text"
            class="evidence-query-input"
            placeholder="errorCode（可选）"
          />
          <button class="btn btn-default btn-sm" @click="openFailedSamplesQuery">
            <i class="h-icon-search"></i>
            查看 POST_FAILED
          </button>
        </div>
      </div>

      <!-- 步骤条 -->
      <div class="steps-container">
        <div class="steps">
          <div
            class="step"
            :class="{ active: currentStep >= 1, completed: currentStep > 1 }"
          >
            <div class="step-number">1</div>
            <div class="step-label">上传文件</div>
          </div>
          <div class="step-line" :class="{ active: currentStep > 1 }"></div>
          <div
            class="step"
            :class="{ active: currentStep >= 2, completed: currentStep > 2 }"
          >
            <div class="step-number">2</div>
            <div class="step-label">预览凭证</div>
          </div>
          <div class="step-line" :class="{ active: currentStep > 2 }"></div>
          <div class="step" :class="{ active: currentStep >= 3 }">
            <div class="step-number">3</div>
            <div class="step-label">完成</div>
          </div>
        </div>
      </div>

      <!-- 第一步：上传文件 -->
      <div v-if="currentStep === 1" class="step-content">
        <div class="upload-section">
          <h3>上传{{ businessTypeLabel }}Excel文件</h3>
          <p class="desc">请上传业务数据Excel文件（.xlsx或.xls格式）</p>

          <div class="form-container">
            <div class="form-row">
              <label><span class="required">*</span> 业务类型：</label>
              <div class="business-type-switch">
                <button
                  class="type-btn"
                  :class="{ active: formData.businessType === 'shop_payment' }"
                  @click="formData.businessType = 'shop_payment'"
                >
                  店铺营收
                </button>
                <button
                  class="type-btn"
                  :class="{ active: formData.businessType === 'shop_collection' }"
                  @click="formData.businessType = 'shop_collection'"
                >
                  店铺回款
                </button>
                <button
                  class="type-btn"
                  :class="{ active: formData.businessType === 'inventory' }"
                  @click="formData.businessType = 'inventory'"
                >
                  进销存
                </button>
                <button
                  class="type-btn"
                  :class="{ active: formData.businessType === 'fund_flow' }"
                  @click="formData.businessType = 'fund_flow'"
                >
                  资金流水
                </button>
              </div>
            </div>

            <!-- 仅在店铺营收模式下显示 -->
            <div v-if="formData.businessType === 'shop_payment' || formData.businessType === 'shop_collection'" class="form-row">
              <label>平台：</label>
              <select v-model="formData.platform" class="form-input">
                <option value="amazon">亚马逊</option>
                <option value="shopify">Shopify</option>
                <option value="ebay">Ebay</option>
                <option value="other">其他</option>
              </select>
            </div>

            <div v-if="formData.businessType === 'fund_flow'" class="form-row">
              <label><span class="required">*</span> 流水平台：</label>
              <select v-model="formData.platform" class="form-input">
                <option value="skyee">SKYEE（收付通）</option>
                <option value="wf">万里汇（WorldFirst）</option>
                <option value="xunhui">寻汇</option>
                <option value="lianlian">连连</option>
              </select>
            </div>

            <div v-if="formData.businessType === 'shop_payment'" class="form-row">
              <label>店铺编码：</label>
              <input
                v-model="formData.storeCode"
                type="text"
                class="form-input"
                placeholder="选填"
              />
            </div>

            <div v-if="formData.businessType === 'shop_payment'" class="form-row">
              <label>店铺名称：</label>
              <input
                v-model="formData.storeName"
                type="text"
                class="form-input"
                placeholder="选填"
              />
            </div>

            <div class="form-row">
              <label><span class="required">*</span> 业务日期：</label>
              <input
                v-model="formData.businessDate"
                type="date"
                class="form-input"
                required
              />
            </div>

            <div class="form-row">
              <label><span class="required">*</span> 选择文件：</label>
              <div class="file-upload">
                <input
                  type="file"
                  ref="fileInput"
                  @change="handleFileChange"
                  accept=".xlsx,.xls"
                  id="file-input"
                />
                <label for="file-input" class="file-label">
                  <i class="h-icon-folder-open"></i>
                  {{ fileName || '选择Excel文件' }}
                </label>
              </div>
            </div>

            <div class="form-actions">
              <button
                class="btn btn-primary"
                @click="handleUpload"
                :disabled="uploading || !selectedFile || !formData.businessDate"
              >
                <i class="h-icon-upload" v-if="!uploading"></i>
                <i class="h-icon-loading" v-if="uploading"></i>
                {{ uploading ? '上传中...' : '上传并解析' }}
              </button>
            </div>
          </div>

          <!-- 使用说明 -->
          <div class="help-section">
            <h4><i class="h-icon-info-circle"></i> 使用说明</h4>
            <ul>
              <li v-if="formData.businessType === 'fund_flow'">1. 请上传平台导出的原始资金流水表，无需额外补“合计”行</li>
              <li v-else>1. Excel文件必须包含"合计"行，系统将自动识别合计行数据</li>
              <li v-if="formData.businessType === 'fund_flow'">2. 系统会按交易类型、描述关键词自动匹配场景；未命中的流水会转入待人工确认</li>
              <li v-else>2. 系统会根据列名自动匹配业务场景（如"订单总额-自营"、"退款-分销"等）</li>
              <li>3. 业务日期用作生成凭证的日期</li>
              <li>4. 上传成功后可预览生成的凭证，确认无误后再保存</li>
            </ul>
          </div>
        </div>
      </div>

      <!-- 第二步：预览凭证 -->
      <div v-if="currentStep === 2" class="step-content">
        <div class="preview-section">
          <h3>凭证预览</h3>
          <p class="desc">
            共解析出 <strong>{{ taskDetail.detailCount }}</strong> 条业务数据
            <span v-if="taskDetail.unmatchedRowCount > 0" class="unmatched-tip">
              （{{ taskDetail.totalRowCount }} 行数据中有 {{ taskDetail.unmatchedRowCount }} 行未自动匹配，已转入待人工确认）
            </span>
            <span v-if="taskDetail.skippedRowCount > 0" class="unmatched-tip">
              （另有 {{ taskDetail.skippedRowCount }} 行按平台规则无需做账，已自动跳过）
            </span>
            <br>
            <span class="status-tip">
              待处理 <strong class="pending">{{ pendingCount }}</strong> 张 /
              已确认 <strong class="confirmed">{{ confirmedCount }}</strong> 张 /
              已删除 <strong class="deleted">{{ deletedCount }}</strong> 张
            </span>
            <span class="edit-tip">（可编辑、确认或删除单张凭证）</span>
          </p>

          <div class="preview-list">
            <div
              v-for="(voucher, index) in voucherPreview"
              :key="index"
              class="voucher-card"
              :class="{ editing: voucher.isEditing }"
            >
              <div class="voucher-header">
                <div class="voucher-title">
                  <strong>{{ voucher.voucherNo }}</strong>
                  <span class="scenario-name">{{ voucher.scenarioName }}</span>
                  <span v-if="voucher.needsReview && voucher.status === 'pending'" class="manual-tag">需确认</span>
                  <span v-if="voucher.status === 'confirmed'" class="confirmed-tag">已确认</span>
                  <span v-if="voucher.status === 'pending' && !voucher.needsReview" class="pending-tag">待处理</span>
                </div>
                <div class="voucher-actions">
                  <template v-if="!voucher.isEditing">
                    <button
                      v-if="voucher.status === 'pending'"
                      class="btn-icon"
                      @click="confirmVoucher(voucher)"
                      title="确认"
                    >
                      <i class="h-icon-check"></i>
                    </button>
                    <button
                      class="btn-icon"
                      @click="startEdit(voucher)"
                      title="编辑"
                    >
                      <i class="h-icon-edit"></i>
                    </button>
                    <button
                      class="btn-icon btn-cancel"
                      @click="deleteVoucher(voucher, index)"
                      title="删除"
                    >
                      <i class="h-icon-close"></i>
                    </button>
                  </template>
                  <template v-else>
                    <button
                      class="btn-icon btn-success"
                      @click="saveEdit(voucher)"
                      title="保存"
                    >
                      <i class="h-icon-check"></i>
                    </button>
                    <button
                      class="btn-icon btn-cancel"
                      @click="cancelEdit(voucher)"
                      title="取消"
                    >
                      <i class="h-icon-close"></i>
                    </button>
                  </template>
                  <span class="voucher-date">{{ formatDate(voucher.date) }}</span>
                </div>
              </div>

              <!-- 摘要 -->
              <div class="voucher-summary">
                <template v-if="!voucher.isEditing">
                  摘要：{{ voucher.summary }}
                </template>
                <template v-else>
                  <label>摘要：</label>
                  <input
                    v-model="voucher.summary"
                    type="text"
                    class="edit-input summary-input"
                    placeholder="请输入摘要"
                  />
                </template>
              </div>

              <div v-if="isBankFlowV1Voucher(voucher)" class="draft-meta-section">
                <div class="draft-meta-grid">
                  <div class="meta-item">
                    <span class="meta-label">事件类型</span>
                    <span class="meta-value">{{ voucher.bankFlowV1.eventType || '-' }}</span>
                  </div>
                  <div class="meta-item">
                    <span class="meta-label">草稿状态</span>
                    <span class="meta-value" :class="'status-' + (voucher.bankFlowV1.voucherDraft.draftStatus || '').toLowerCase()">
                      {{ voucher.bankFlowV1.voucherDraft.draftStatus || '-' }}
                    </span>
                  </div>
                  <div class="meta-item">
                    <span class="meta-label">关系对象</span>
                    <span class="meta-value">{{ voucher.bankFlowV1.relationContext.entityName || '-' }}</span>
                  </div>
                  <div class="meta-item">
                    <span class="meta-label">关系状态</span>
                    <span class="meta-value">
                      {{ voucher.bankFlowV1.relationContext.primaryRole || '-' }} /
                      {{ voucher.bankFlowV1.relationContext.relationshipStatus || '-' }} /
                      {{ voucher.bankFlowV1.relationContext.entityMatchStatus || '-' }}
                    </span>
                  </div>
                </div>
                <div v-if="voucher.bankFlowV1.accountingDecision.decisionReason" class="meta-text">
                  <strong>决策说明：</strong>{{ voucher.bankFlowV1.accountingDecision.decisionReason }}
                </div>
                <div v-if="voucher.bankFlowV1.accountingDecision.riskFlags && voucher.bankFlowV1.accountingDecision.riskFlags.length" class="meta-tags risk">
                  <span class="tag-label">风险提示</span>
                  <span v-for="flag in voucher.bankFlowV1.accountingDecision.riskFlags" :key="flag" class="meta-tag risk" :title="flag">{{ formatLlmRiskWarning(flag) }}</span>
                </div>
                <div v-if="voucher.bankFlowV1.voucherDraft.reviewHints && voucher.bankFlowV1.voucherDraft.reviewHints.length" class="meta-tags hint">
                  <span class="tag-label">审核提示</span>
                  <span v-for="(hint, hintIndex) in voucher.bankFlowV1.voucherDraft.reviewHints" :key="hintIndex" class="meta-tag hint">{{ hint }}</span>
                </div>
                <div v-if="voucher.bankFlowV1.voucherDraft.blockedReasons && voucher.bankFlowV1.voucherDraft.blockedReasons.length" class="meta-tags blocked">
                  <span class="tag-label">阻断原因</span>
                  <span v-for="reason in voucher.bankFlowV1.voucherDraft.blockedReasons" :key="reason" class="meta-tag blocked">{{ reason }}</span>
                </div>
                <div v-if="voucher.bankFlowV1.referenceFactContext && voucher.bankFlowV1.referenceFactContext.factMatchStatus && voucher.bankFlowV1.referenceFactContext.factMatchStatus !== 'NOT_FOUND'" class="reference-fact-section">
                  <div class="reference-fact-header">
                    <i class="h-icon-document"></i>
                    <span class="rf-title">参考事实解释</span>
                    <span class="rf-status" :class="'rf-' + (voucher.bankFlowV1.referenceFactContext.factMatchStatus || '').toLowerCase()">
                      {{ formatFactMatchStatus(voucher.bankFlowV1.referenceFactContext.factMatchStatus) }}
                    </span>
                  </div>
                  <div class="reference-fact-body">
                    <div v-if="voucher.bankFlowV1.referenceFactContext.matchedFacts && voucher.bankFlowV1.referenceFactContext.matchedFacts.length" class="rf-facts">
                      <div v-for="(fact, factIdx) in voucher.bankFlowV1.referenceFactContext.matchedFacts" :key="factIdx" class="rf-fact-item">
                        <div class="rf-fact-row"><span class="rf-label">参考类型</span><span class="rf-value">{{ fact.factType || '-' }}</span></div>
                        <div class="rf-fact-row"><span class="rf-label">期间</span><span class="rf-value">{{ fact.period || '-' }}</span></div>
                        <div class="rf-fact-row"><span class="rf-label">对象名称</span><span class="rf-value">{{ fact.counterpartyNameSnapshot || '-' }}</span></div>
                        <div class="rf-fact-row"><span class="rf-label">支付金额</span><span class="rf-value">{{ fact.paymentAmount ? formatMoney(fact.paymentAmount) : '-' }}</span></div>
                        <div class="rf-fact-row"><span class="rf-label">期末余额</span><span class="rf-value">{{ fact.endingBalance ? formatMoney(fact.endingBalance) : '-' }}</span></div>
                        <div v-if="fact.amountItems" class="rf-fact-row"><span class="rf-label">金额项</span><span class="rf-value">{{ formatJsonValue(fact.amountItems) }}</span></div>
                      </div>
                    </div>
                    <div v-if="voucher.bankFlowV1.referenceFactContext.amountMatchResult" class="rf-match">
                      <div class="rf-match-header">金额匹配</div>
                      <div class="rf-match-row"><span class="rf-label">匹配类型</span>
                        <span class="rf-match-type" :class="'match-' + (voucher.bankFlowV1.referenceFactContext.amountMatchResult.matchType || '').toLowerCase()">
                          {{ formatMatchType(voucher.bankFlowV1.referenceFactContext.amountMatchResult.matchType) }}
                        </span>
                      </div>
                      <div v-if="voucher.bankFlowV1.referenceFactContext.amountMatchResult.matchedField" class="rf-match-row">
                        <span class="rf-label">匹配字段</span><span class="rf-value">{{ voucher.bankFlowV1.referenceFactContext.amountMatchResult.matchedField }}</span>
                      </div>
                      <div v-if="voucher.bankFlowV1.referenceFactContext.amountMatchResult.factAmount" class="rf-match-row">
                        <span class="rf-label">参考金额</span><span class="rf-value">{{ formatMoney(voucher.bankFlowV1.referenceFactContext.amountMatchResult.factAmount) }}</span>
                      </div>
                      <div v-if="voucher.bankFlowV1.referenceFactContext.amountMatchResult.bankFlowAmount" class="rf-match-row">
                        <span class="rf-label">流水金额</span><span class="rf-value">{{ formatMoney(voucher.bankFlowV1.referenceFactContext.amountMatchResult.bankFlowAmount) }}</span>
                      </div>
                      <div v-if="voucher.bankFlowV1.referenceFactContext.amountMatchResult.difference" class="rf-match-row">
                        <span class="rf-label">差额</span><span class="rf-value">{{ formatMoney(voucher.bankFlowV1.referenceFactContext.amountMatchResult.difference) }}</span>
                      </div>
                    </div>
                    <div v-if="voucher.bankFlowV1.referenceFactContext.explanation && voucher.bankFlowV1.referenceFactContext.explanation.summary" class="rf-explanation">
                      <div class="rf-explanation-header">解释说明</div>
                      <div class="rf-explanation-summary">{{ voucher.bankFlowV1.referenceFactContext.explanation.summary }}</div>
                      <div v-if="voucher.bankFlowV1.referenceFactContext.explanation.highlights && voucher.bankFlowV1.referenceFactContext.explanation.highlights.length" class="rf-explanation-highlights">
                        <span v-for="(hl, hlIdx) in voucher.bankFlowV1.referenceFactContext.explanation.highlights" :key="hlIdx" class="rf-highlight-tag">{{ hl }}</span>
                      </div>
                    </div>
                    <div v-if="voucher.bankFlowV1.referenceFactContext.riskFlags && voucher.bankFlowV1.referenceFactContext.riskFlags.length" class="rf-risks">
                      <span class="tag-label">参考事实风险</span>
                      <span v-for="flag in voucher.bankFlowV1.referenceFactContext.riskFlags" :key="flag" class="meta-tag risk">{{ flag }}</span>
                    </div>
                  </div>
                </div>
                <div v-else-if="voucher.bankFlowV1.referenceFactContext && voucher.bankFlowV1.referenceFactContext.factMatchStatus === 'NOT_FOUND'" class="reference-fact-section empty">
                  <div class="reference-fact-header">
                    <i class="h-icon-document"></i>
                    <span class="rf-title">参考事实解释</span>
                    <span class="rf-status rf-not_found">暂无可用参考事实</span>
                  </div>
                </div>
                <div v-if="voucher.bankFlowV1.reviewFeedback && voucher.bankFlowV1.reviewFeedback.finalDecision" class="meta-text feedback">
                  <strong>最近审核：</strong>{{ voucher.bankFlowV1.reviewFeedback.finalDecision }}
                </div>
                <div v-if="voucher.bankFlowV1.llmDraftAdvice && voucher.bankFlowV1.llmDraftAdvice.enabled" class="llm-draft-section" :class="'llm-section-' + llmDraftStatusClass(voucher.bankFlowV1.llmDraftAdvice)">
                  <div class="llm-draft-header">
                    <i class="h-icon-ai"></i>
                    <span>AI 草稿建议</span>
                  </div>
                  <div class="llm-draft-status-row">
                    <span class="llm-status-badge" :class="'llm-badge-' + llmDraftStatusClass(voucher.bankFlowV1.llmDraftAdvice)">
                      {{ llmDraftStatusLabel(voucher.bankFlowV1.llmDraftAdvice) }}
                    </span>
                  </div>
                  <div v-if="voucher.bankFlowV1.llmDraftAdvice.riskWarnings && voucher.bankFlowV1.llmDraftAdvice.riskWarnings.length" class="llm-risk-section">
                    <div class="llm-risk-label">风险提示：</div>
                    <ul class="llm-risk-list">
                      <li v-for="(flag, idx) in voucher.bankFlowV1.llmDraftAdvice.riskWarnings" :key="'llm-risk-' + idx" class="llm-risk-item" :title="flag">
                        {{ formatLlmRiskWarning(flag) }}
                      </li>
                    </ul>
                  </div>
                  <div v-if="voucher.bankFlowV1.llmDraftAdvice.status !== 'SUCCESS'" class="llm-skipped-reason">
                    {{ llmSkippedReason(voucher.bankFlowV1.llmDraftAdvice, voucher.bankFlowV1.accountingDecision && voucher.bankFlowV1.accountingDecision.riskFlags) }}
                  </div>
                  <div v-if="voucher.bankFlowV1.llmDraftAdvice.summarySuggestion" class="llm-draft-text">
                    <strong>摘要建议：</strong>{{ voucher.bankFlowV1.llmDraftAdvice.summarySuggestion }}
                  </div>
                  <div v-if="voucher.bankFlowV1.llmDraftAdvice.auditExplanation" class="llm-draft-text">
                    <strong>AI 说明：</strong>{{ voucher.bankFlowV1.llmDraftAdvice.auditExplanation }}
                  </div>
                  <div v-if="voucher.bankFlowV1.llmDraftAdvice.reviewHints && voucher.bankFlowV1.llmDraftAdvice.reviewHints.length" class="meta-tags hint">
                    <span class="tag-label">AI 提示</span>
                    <span v-for="(hint, idx) in voucher.bankFlowV1.llmDraftAdvice.reviewHints" :key="'llm-hint-' + idx" class="meta-tag hint">{{ hint }}</span>
                  </div>
                  <div v-if="voucher.bankFlowV1.llmDraftAdvice.validationErrors && voucher.bankFlowV1.llmDraftAdvice.validationErrors.length" class="llm-validation-errors">
                    <div class="llm-val-title">安全校验：</div>
                    <div class="llm-val-text">AI 草稿未通过安全校验，已退回规则/人工处理。</div>
                  </div>
                  <div class="llm-draft-lines-header">建议分录：</div>
                  <table v-if="voucher.bankFlowV1.llmDraftAdvice.voucherLines && voucher.bankFlowV1.llmDraftAdvice.voucherLines.length" class="llm-draft-table">
                    <thead>
                      <tr><th>#</th><th>方向</th><th>科目</th><th>金额</th><th>币种</th><th>辅助提示</th><th>行说明</th></tr>
                    </thead>
                    <tbody>
                      <tr v-for="line in voucher.bankFlowV1.llmDraftAdvice.voucherLines" :key="'llm-line-' + line.entryNo">
                        <td>{{ line.entryNo }}</td>
                        <td>{{ line.side }}</td>
                        <td>{{ line.subjectCode }} {{ line.subjectName }}</td>
                        <td class="text-right">{{ formatMoney(line.amount) }}</td>
                        <td>{{ line.currency || '-' }}</td>
                        <td>{{ line.auxiliary || '-' }}</td>
                        <td>{{ line.lineReason || '-' }}</td>
                      </tr>
                    </tbody>
                  </table>
                  <div v-else class="llm-no-lines">暂无 AI 草稿分录，请人工处理。</div>
                  <details class="llm-draft-tech-details">
                    <summary>技术详情</summary>
                    <div class="llm-draft-meta">
                      <span class="meta-chip">状态: {{ formatLlmAdviceStatus(voucher.bankFlowV1.llmDraftAdvice.status) }}</span>
                      <span v-if="voucher.bankFlowV1.llmDraftAdvice.validationStatus" class="meta-chip">校验: {{ voucher.bankFlowV1.llmDraftAdvice.validationStatus }}</span>
                      <span v-if="voucher.bankFlowV1.llmDraftAdvice.confidence !== null && voucher.bankFlowV1.llmDraftAdvice.confidence !== undefined" class="meta-chip">置信度: {{ formatLlmConfidence(voucher.bankFlowV1.llmDraftAdvice.confidence) }}</span>
                      <span v-if="voucher.bankFlowV1.llmDraftAdvice.modelName" class="meta-chip">模型: {{ voucher.bankFlowV1.llmDraftAdvice.modelName }}</span>
                      <span v-if="voucher.bankFlowV1.llmDraftAdvice.promptVersion" class="meta-chip">Prompt: {{ voucher.bankFlowV1.llmDraftAdvice.promptVersion }}</span>
                    </div>
                  </details>
                  <div class="llm-draft-disclaimer">提示：本建议仅供审核参考，不会自动入账。</div>
                </div>
                <!-- Decision Memory Review Card -->
                <div v-if="isBankFlowV1Voucher(voucher) && voucher.detailId">
                  <decision-memory-review-card
                    :detail-id="voucher.detailId"
                    :view="memoryReviewViews[voucher.voucherKey || String(voucher.detailId)]"
                    @refreshed="loadMemoryReviewView(voucher)"
                    @modify="handleMemoryReviewModify"
                    @error="handleMemoryReviewError"
                  />
                </div>
                <!-- Memory Review Diff Visualization -->
                <div v-if="memoryReviewDiffs[voucher.voucherKey || String(voucher.detailId)]" class="memory-diff-section">
                  <div class="diff-header">
                    <i class="h-icon-diff"></i>
                    <span>系统历史建议 vs 人工确认结果</span>
                  </div>

                  <!-- 系统建议行 -->
                  <details class="diff-details" :open="memoryReviewDiffs[voucher.voucherKey || String(voucher.detailId)].hasDiffs">
                    <summary>系统历史建议分录（{{ memoryReviewDiffs[voucher.voucherKey || String(voucher.detailId)].systemSuggestedLines.length }} 行）</summary>
                    <table class="diff-table full-line-table">
                      <thead><tr><th>#</th><th>方向</th><th>科目代码</th><th>科目名称</th><th>金额</th><th>辅助核算</th><th>币种</th><th>摘要</th></tr></thead>
                      <tbody>
                        <tr v-for="line in memoryReviewDiffs[voucher.voucherKey || String(voucher.detailId)].systemSuggestedLines" :key="'sys-' + line.entryNo" class="diff-row suggested">
                          <td>{{ line.entryNo }}</td>
                          <td>{{ line.side }}</td>
                          <td class="mono">{{ line.subjectCode || '-' }}</td>
                          <td>{{ line.subjectName || '-' }}</td>
                          <td class="text-right">{{ formatMoney(line.amount) }}</td>
                          <td class="mono">{{ line.auxiliary || '-' }}</td>
                          <td>{{ line.currency || 'CNY' }}</td>
                          <td class="mono">{{ line.lineReason || '-' }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </details>

                  <!-- 人工确认行 -->
                  <details class="diff-details" :open="memoryReviewDiffs[voucher.voucherKey || String(voucher.detailId)].hasDiffs">
                    <summary>人工确认分录（{{ memoryReviewDiffs[voucher.voucherKey || String(voucher.detailId)].reviewedLines.length }} 行）</summary>
                    <table class="diff-table full-line-table">
                      <thead><tr><th>#</th><th>方向</th><th>科目代码</th><th>科目名称</th><th>金额</th><th>辅助核算</th><th>币种</th><th>摘要</th></tr></thead>
                      <tbody>
                        <tr v-for="line in memoryReviewDiffs[voucher.voucherKey || String(voucher.detailId)].reviewedLines" :key="'rev-' + line.entryNo" class="diff-row reviewed">
                          <td>{{ line.entryNo }}</td>
                          <td>{{ line.side }}</td>
                          <td class="mono">{{ line.subjectCode || '-' }}</td>
                          <td>{{ line.subjectName || '-' }}</td>
                          <td class="text-right">{{ formatMoney(line.amount) }}</td>
                          <td class="mono">{{ line.auxiliary || '-' }}</td>
                          <td>{{ line.currency || 'CNY' }}</td>
                          <td class="mono">{{ line.lineReason || '-' }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </details>

                  <!-- 字段级差异摘要 -->
                  <div v-if="memoryReviewDiffs[voucher.voucherKey || String(voucher.detailId)].hasDiffs" class="diff-summary">
                    <div class="diff-subheader">字段变更（{{ memoryReviewDiffs[voucher.voucherKey || String(voucher.detailId)].lineDiffs.length }} 项）</div>
                    <table class="diff-table diff-field-table">
                      <thead><tr><th>#</th><th>方向</th><th>字段</th><th>系统历史建议</th><th>人工确认结果</th></tr></thead>
                      <tbody>
                        <tr v-for="diff in memoryReviewDiffs[voucher.voucherKey || String(voucher.detailId)].lineDiffs" :key="diff.entryNo + '-' + diff.field" class="diff-row">
                          <td>{{ diff.entryNo }}</td>
                          <td>{{ diff.side }}</td>
                          <td>{{ diffFieldLabel(diff.field) }}</td>
                          <td class="diff-old">{{ diff.oldValue || '-' }}</td>
                          <td class="diff-new">{{ diff.newValue || '-' }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <!-- 无差异提示 -->
                  <div v-else class="diff-no-change">
                    <i class="h-icon-check"></i>
                    人工确认结果与系统建议一致，无字段差异
                  </div>
                </div>
                <div class="evidence-chain-actions">
                  <button class="btn btn-default btn-sm" @click="openEvidenceChainByVoucher(voucher)">
                    <i class="h-icon-eye"></i>
                    查看证据链
                  </button>
                  <button
                    v-if="hasPostingFailedStage(voucher)"
                    class="btn btn-default btn-sm danger"
                    @click="openFailedSampleByVoucher(voucher)"
                  >
                    <i class="h-icon-warning"></i>
                    查看失败样本
                  </button>
                </div>
              </div>

              <table class="voucher-table">
                <thead>
                  <tr>
                    <th width="40%">科目</th>
                    <th width="26%" class="text-right">借方金额</th>
                    <th width="26%" class="text-right">贷方金额</th>
                    <th v-if="voucher.isEditing" width="8%"></th>
                  </tr>
                </thead>
                <tbody>
                  <template v-for="(entry, entryIndex) in voucher.entries">
                    <tr :key="'entry-' + entryIndex">
                      <td>
                        <template v-if="!voucher.isEditing">
                          <span class="subject-code">{{ entry.subjectCode }}</span>
                          <span class="subject-name">{{ entry.subjectName }}</span>
                          <div v-if="isBankFlowV1Voucher(voucher) && entry.subjectCandidateType && entry.subjectCandidateType.length" class="entry-candidate-types">
                            候选：{{ entry.subjectCandidateType.join(' / ') }}
                          </div>
                          <div v-if="isBankFlowV1Voucher(voucher) && entry.auxiliaryRequirement && entry.auxiliaryRequirement.required" class="entry-auxiliary-readonly">
                            辅助核算：{{ formatAuxiliary(entry.selectedAuxiliary) || '未选择' }}
                          </div>
                          <div v-if="isBankFlowV1Voucher(voucher) && entry.lineReason" class="entry-line-reason">
                            {{ entry.lineReason }}
                          </div>
                        </template>
                        <template v-else>
                          <div class="subject-edit-wrap">
                            <div class="subject-search-box">
                              <input
                                v-model="entry.subjectCode"
                                type="text"
                                class="edit-input code-input"
                                placeholder="科目代码"
                                :disabled="!isEntryFieldEditable(voucher, entryIndex, 'subjectCode')"
                                @input="onSubjectCodeInput(entry)"
                                @blur="closeSubjectDropdown(entry)"
                              />
                              <div v-if="entry._subjectDropdown && entry._subjectDropdown.length" class="subject-dropdown">
                                <div
                                  v-for="s in entry._subjectDropdown"
                                  :key="s.id"
                                  class="subject-option"
                                  @mousedown.prevent="selectSubject(entry, s)"
                                >{{ s.code }} {{ s.name }}</div>
                              </div>
                            </div>
                            <input
                              v-model="entry.subjectName"
                              type="text"
                              class="edit-input name-input"
                              placeholder="科目名称"
                              :disabled="!isEntryFieldEditable(voucher, entryIndex, 'subjectName')"
                            />
                          </div>
                          <div v-if="isBankFlowV1Voucher(voucher) && entry.subjectCandidateType && entry.subjectCandidateType.length" class="entry-candidate-types">
                            候选：{{ entry.subjectCandidateType.join(' / ') }}
                          </div>
                          <div v-if="isBankFlowV1Voucher(voucher) && entry.auxiliaryRequirement && entry.auxiliaryRequirement.required" class="entry-auxiliary-editor">
                            <div class="aux-title">辅助核算（必填）</div>
                            <div class="aux-fields">
                              <input
                                v-model="entry.selectedAuxiliary.categoryId"
                                type="number"
                                class="edit-input aux-input"
                                placeholder="categoryId"
                                :disabled="!isEntryFieldEditable(voucher, entryIndex, 'selectedAuxiliary')"
                              />
                              <input
                                v-model="entry.selectedAuxiliary.detailId"
                                type="number"
                                class="edit-input aux-input"
                                placeholder="detailId"
                                :disabled="!isEntryFieldEditable(voucher, entryIndex, 'selectedAuxiliary')"
                              />
                              <input
                                v-model="entry.selectedAuxiliary.anchorEntityId"
                                type="text"
                                class="edit-input aux-input"
                                placeholder="anchorEntityId"
                                :disabled="!isEntryFieldEditable(voucher, entryIndex, 'selectedAuxiliary')"
                              />
                            </div>
                          </div>
                          <div v-if="isBankFlowV1Voucher(voucher) && entry.lineReason" class="entry-line-reason">
                            {{ entry.lineReason }}
                          </div>
                        </template>
                      </td>
                      <td class="text-right">
                        <template v-if="!voucher.isEditing">
                          {{ entry.debit > 0 ? formatMoney(entry.debit) : '' }}
                        </template>
                        <input
                          v-else
                          v-model="entry.debit"
                          type="number"
                          step="0.01"
                          class="edit-input amount-input"
                          @input="onDebitInput(voucher, entry)"
                        />
                      </td>
                      <td class="text-right">
                        <template v-if="!voucher.isEditing">
                          {{ entry.credit > 0 ? formatMoney(entry.credit) : '' }}
                        </template>
                        <input
                          v-else
                          v-model="entry.credit"
                          type="number"
                          step="0.01"
                          class="edit-input amount-input"
                          @input="onCreditInput(voucher, entry)"
                        />
                      </td>
                      <td v-if="voucher.isEditing" class="action-cell">
                        <button class="btn-row-del" @click="removeEntry(voucher, entryIndex)" title="删除行">×</button>
                      </td>
                    </tr>
                    <!-- 外币行：科目配置了币种核算时显示 -->
                    <tr
                      v-if="voucher.isEditing && resolveSubjectCurrencies(entry).length"
                      :key="'cur-' + entryIndex"
                      class="currency-sub-row"
                    >
                      <td colspan="4">
                        <div class="foreign-fields">
                          <select v-model="entry.currencyId" class="currency-select" @change="onCurrencyChange(entry)">
                            <option :value="null">-- 选择外币 --</option>
                            <option v-for="c in resolveSubjectCurrencies(entry)" :key="c.id" :value="c.id">
                              {{ c.name }}({{ c.code }})
                            </option>
                          </select>
                          <template v-if="entry.currencyId">
                            <input
                              v-model="entry.originalAmount"
                              type="number"
                              step="0.01"
                              class="edit-input foreign-input"
                              placeholder="原币金额"
                              @input="syncForeignAmount(voucher, entry)"
                            />
                            <input
                              v-model="entry.exchangeRate"
                              type="number"
                              step="0.00000001"
                              class="edit-input foreign-input"
                              placeholder="汇率"
                              @input="syncForeignAmount(voucher, entry)"
                            />
                            <button
                              class="btn-get-rate"
                              :disabled="!!entry._rateLoading"
                              @click="fetchCurrentRate(entry)"
                            >今汇</button>
                          </template>
                        </div>
                      </td>
                    </tr>
                  </template>
                </tbody>
                <tfoot v-if="voucher.isEditing">
                  <tr class="add-row-tr">
                    <td colspan="4">
                      <button class="btn-add-row" @click="addEntry(voucher)">
                        <i class="h-icon-plus"></i> 新增行
                      </button>
                    </td>
                  </tr>
                  <tr class="balance-row" :class="{ unbalanced: !voucher.isBalanced }">
                    <td>借贷平衡检查</td>
                    <td class="text-right">{{ formatMoney(calculateDebitTotal(voucher)) }}</td>
                    <td class="text-right">{{ formatMoney(calculateCreditTotal(voucher)) }}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>

              <div v-if="isBankFlowV1Voucher(voucher) && voucher.isEditing" class="review-edit-section">
                <div class="review-row">
                  <label>审核动作</label>
                  <input value="APPROVE" class="edit-input review-input" disabled />
                </div>
                <div class="review-row">
                  <label>变更原因</label>
                  <input
                    v-model="voucher.changeReason"
                    type="text"
                    class="edit-input review-input"
                    placeholder="请输入变更原因"
                  />
                </div>
                <div class="review-row">
                  <label>最终结论</label>
                  <input
                    v-model="voucher.finalDecision"
                    type="text"
                    class="edit-input review-input"
                    placeholder="请输入最终结论"
                  />
                </div>
              </div>
            </div>
          </div>

          <div class="preview-stats">
            <div class="stats-bar">
              <span>共 <strong>{{ taskDetail.detailCount }}</strong> 条</span>
              <span class="stat-item pending">待处理 <strong>{{ pendingCount }}</strong></span>
              <span class="stat-item confirmed">已确认 <strong>{{ confirmedCount }}</strong></span>
              <span class="stat-item deleted">已删除 <strong>{{ deletedCount }}</strong></span>
            </div>
            <div v-if="!allProcessed" class="process-tip">
              <i class="h-icon-info-circle"></i>
              还有 {{ pendingCount }} 张凭证未处理，请逐一确认或删除
            </div>
          </div>

          <div class="preview-actions">
            <button class="btn btn-default" @click="handleBack">
              <i class="h-icon-arrow-left"></i>
              返回上一步
            </button>
            <button class="btn btn-success" @click="confirmAllPending" :disabled="pendingCount === 0">
              <i class="h-icon-check"></i>
              一键确认全部（{{ pendingCount }}）
            </button>
            <button class="btn btn-primary" @click="handleGenerate" :disabled="!canGenerate">
              <i class="h-icon-check" v-if="!generating"></i>
              <i class="h-icon-loading" v-if="generating"></i>
              {{ generating ? '生成中...' : `确认生成凭证（${confirmedCount}张）` }}
            </button>
          </div>
        </div>
      </div>

      <!-- 第三步：完成 -->
      <div v-if="currentStep === 3" class="step-content">
        <div class="success-section">
          <div class="success-icon">
            <i class="h-icon-check-circle"></i>
          </div>
          <h3>凭证生成成功！</h3>
          <p class="desc">
            已成功生成 <strong>{{ generateResult.voucherCount }}</strong> 张凭证
          </p>

          <div class="success-actions">
            <button class="btn btn-primary" @click="goToVoucherList">
              <i class="h-icon-list"></i>
              查看凭证列表
            </button>
            <button class="btn btn-default" @click="handleReset">
              <i class="h-icon-plus"></i>
              继续上传
            </button>
          </div>
        </div>
      </div>

      <div
        v-if="evidenceDialogVisible"
        ref="evidenceChainOverlay"
        class="evidence-chain-overlay"
        @click="closeEvidenceChainDialog"
      >
        <div class="evidence-chain-drawer" @click.stop>
          <div class="evidence-chain-header">
            <div>
              <h3>证据链详情</h3>
              <p>只读展示，支持按 voucherId 或 traceId 查询</p>
            </div>
            <button class="evidence-close-btn" @click="closeEvidenceChainDialog">
              <i class="h-icon-close"></i>
            </button>
          </div>

          <div class="evidence-chain-toolbar">
            <div class="evidence-chain-query-info">
              <span v-if="evidenceQueryState.voucherId" class="query-chip">voucherId: {{ evidenceQueryState.voucherId }}</span>
              <span v-if="evidenceQueryState.traceId" class="query-chip">traceId: {{ evidenceQueryState.traceId }}</span>
              <span v-if="evidenceChainMeta.resultCount !== undefined" class="query-chip muted">阶段数 {{ evidenceChainMeta.resultCount }}</span>
            </div>
          </div>

          <div v-if="evidenceLoading" class="evidence-loading">
            <i class="h-icon-loading"></i>
            正在加载证据链...
          </div>

          <div v-else class="evidence-chain-body">
            <div v-if="evidenceChainError" class="evidence-empty-state error">
              <i class="h-icon-warning"></i>
              <div class="empty-title">证据链加载失败</div>
              <div class="empty-desc">{{ evidenceChainError }}</div>
            </div>

            <template v-else>
              <div v-if="!evidenceChainStages.length" class="evidence-empty-state">
                <i class="h-icon-document"></i>
                <div class="empty-title">暂无证据链数据</div>
                <div class="empty-desc">请尝试输入 voucherId 或 traceId 后重新查询。</div>
              </div>

              <div v-else class="evidence-stage-list">
                <div
                  v-for="(stage, index) in evidenceChainStages"
                  :key="stage._stageKey || index"
                  class="evidence-stage-card"
                  :class="stage.snapshotStageClass"
                >
                  <div class="stage-card-header" @click="toggleEvidenceStage(stage)">
                    <div class="stage-card-title">
                      <span class="stage-badge">{{ stage.snapshotStage }}</span>
                      <span class="stage-seq">#{{ stage.snapshotSequence }}</span>
                      <span class="stage-time">{{ formatDateTime(stage.createdAt) }}</span>
                    </div>
                    <div class="stage-card-summary">
                      <span>eventType: {{ stage.eventType || '-' }}</span>
                      <span>draftStatus: {{ stage.draftStatus || '-' }}</span>
                      <span>reviewAction: {{ stage.reviewAction || '-' }}</span>
                      <span>postingStatus: {{ stage.postingStatus || '-' }}</span>
                    </div>
                    <button class="stage-toggle-btn" @click.stop="toggleEvidenceStage(stage)">
                      {{ stage.expanded ? '收起' : '展开' }}
                    </button>
                  </div>

                  <div class="stage-card-body" v-if="stage.expanded">
                    <div class="stage-grid">
                      <div class="stage-field">
                        <span class="field-label">snapshotStage</span>
                        <span class="field-value">{{ stage.snapshotStage || '-' }}</span>
                      </div>
                      <div class="stage-field">
                        <span class="field-label">snapshotSequence</span>
                        <span class="field-value">{{ stage.snapshotSequence !== null && stage.snapshotSequence !== undefined ? stage.snapshotSequence : '-' }}</span>
                      </div>
                      <div class="stage-field">
                        <span class="field-label">createdAt</span>
                        <span class="field-value">{{ formatDateTime(stage.createdAt) }}</span>
                      </div>
                      <div class="stage-field">
                        <span class="field-label">eventType</span>
                        <span class="field-value">{{ stage.eventType || '-' }}</span>
                      </div>
                      <div class="stage-field">
                        <span class="field-label">decisionReason</span>
                        <span class="field-value">{{ stage.decisionReason || '-' }}</span>
                      </div>
                      <div class="stage-field">
                        <span class="field-label">draftStatus</span>
                        <span class="field-value">{{ stage.draftStatus || '-' }}</span>
                      </div>
                      <div class="stage-field">
                        <span class="field-label">reviewAction</span>
                        <span class="field-value">{{ stage.reviewAction || '-' }}</span>
                      </div>
                      <div class="stage-field">
                        <span class="field-label">postingStatus</span>
                        <span class="field-value">{{ stage.postingStatus || '-' }}</span>
                      </div>
                      <div class="stage-field">
                        <span class="field-label">voucherId</span>
                        <span class="field-value">{{ stage.voucherId || '-' }}</span>
                      </div>
                      <div class="stage-field">
                        <span class="field-label">voucherWord</span>
                        <span class="field-value">{{ stage.voucherWord || '-' }}</span>
                      </div>
                      <div class="stage-field">
                        <span class="field-label">voucherCode</span>
                        <span class="field-value">{{ stage.voucherCode || '-' }}</span>
                      </div>
                    </div>

                    <div v-if="stage.errors && stage.errors.length" class="stage-errors">
                      <span class="field-label">errors</span>
                      <div class="error-tags">
                        <span v-for="(error, errorIndex) in stage.errors" :key="errorIndex" class="error-tag">
                          {{ formatEvidenceError(error) }}
                        </span>
                      </div>
                    </div>

                    <div class="stage-detail-actions">
                      <button
                        v-if="stage.snapshotStage === 'POST_FAILED' || stage.postingStatus === 'POST_FAILED'"
                        class="btn btn-default btn-sm danger"
                        @click.stop="openFailedSample(stage)"
                      >
                        查看失败样本
                      </button>
                    </div>

                    <div class="stage-detail-panels">
                      <div class="detail-panel">
                        <div class="detail-title">sourceRecord</div>
                        <pre>{{ formatMaskedJson(stage.sourceRecord) }}</pre>
                      </div>
                      <div class="detail-panel">
                        <div class="detail-title">businessEvidence</div>
                        <pre>{{ formatMaskedJson(stage.businessEvidence) }}</pre>
                      </div>
                      <div class="detail-panel">
                        <div class="detail-title">relationContext</div>
                        <pre>{{ formatMaskedJson(stage.relationContext) }}</pre>
                      </div>
                      <div class="detail-panel">
                        <div class="detail-title">referenceFactContext</div>
                        <pre>{{ formatMaskedJson(stage.referenceFactContext) }}</pre>
                      </div>
                      <div class="detail-panel">
                        <div class="detail-title">accountingEvent</div>
                        <pre>{{ formatMaskedJson(stage.accountingEvent) }}</pre>
                      </div>
                      <div class="detail-panel">
                        <div class="detail-title">accountingDecision</div>
                        <pre>{{ formatMaskedJson(stage.accountingDecision) }}</pre>
                      </div>
                      <div class="detail-panel">
                        <div class="detail-title">voucherDraft</div>
                        <pre>{{ formatMaskedJson(stage.voucherDraft) }}</pre>
                      </div>
                      <div class="detail-panel">
                        <div class="detail-title">reviewFeedback</div>
                        <pre>{{ formatMaskedJson(stage.reviewFeedback) }}</pre>
                      </div>
                      <div class="detail-panel">
                        <div class="detail-title">postingResult</div>
                        <pre>{{ formatMaskedJson(stage.postingResult) }}</pre>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>

      <div
        v-if="failedSampleDialogVisible"
        ref="failedSampleOverlay"
        class="evidence-sample-overlay"
        @click="closeFailedSampleDialog"
      >
        <div class="evidence-sample-dialog" @click.stop>
          <div class="evidence-sample-header">
            <div>
              <h3>POST_FAILED 最小样本</h3>
              <p>仅展示失败定位所需的关键信息</p>
            </div>
            <button class="evidence-close-btn" @click="closeFailedSampleDialog">
              <i class="h-icon-close"></i>
            </button>
          </div>

          <div v-if="failedSampleMode === 'list'" class="evidence-sample-body">
            <div class="failed-query-inline">
              <input
                v-model="failedSampleQuery.startDate"
                type="date"
                class="evidence-query-input"
                placeholder="开始日期"
              />
              <input
                v-model="failedSampleQuery.endDate"
                type="date"
                class="evidence-query-input"
                placeholder="结束日期"
              />
              <input
                v-model.trim="failedSampleQuery.eventType"
                type="text"
                class="evidence-query-input"
                placeholder="eventType（可选）"
              />
              <input
                v-model.trim="failedSampleQuery.errorCode"
                type="text"
                class="evidence-query-input"
                placeholder="errorCode（可选）"
              />
              <button class="btn btn-default btn-sm" :disabled="failedSampleLoading" @click="queryFailedSamples">
                <i class="h-icon-search"></i>
                {{ failedSampleLoading ? '查询中...' : '刷新样本' }}
              </button>
            </div>
            <div v-if="failedSampleError" class="evidence-empty-state error">
              <i class="h-icon-warning"></i>
              <div class="empty-title">失败样本加载失败</div>
              <div class="empty-desc">{{ failedSampleError }}</div>
            </div>
            <div v-else-if="failedSampleLoading" class="evidence-loading">
              <i class="h-icon-loading"></i>
              正在加载失败样本...
            </div>
            <div v-else-if="!failedSampleList.length" class="evidence-empty-state">
              <i class="h-icon-document"></i>
              <div class="empty-title">暂无失败样本</div>
              <div class="empty-desc">当前时间范围内没有 POST_FAILED 记录。</div>
            </div>
            <div v-else class="failed-sample-list">
              <div v-for="(sample, sampleIndex) in failedSampleList" :key="sample._sampleKey || sampleIndex" class="failed-sample-card">
                <div class="sample-summary">
                  <div class="sample-chip">stage: {{ sample.snapshotStage || '-' }}</div>
                  <div class="sample-chip">postingStatus: {{ sample.postingStatus || '-' }}</div>
                  <div class="sample-chip">traceId: {{ sample.traceId || '-' }}</div>
                  <div class="sample-chip">voucherId: {{ sample.finalVoucherId || '-' }}</div>
                </div>
                <div class="sample-block">
                  <div class="sample-title">summary</div>
                  <div class="sample-text">{{ sample.summary || '-' }}</div>
                </div>
                <div class="sample-block">
                  <div class="sample-title">错误信息</div>
                  <div class="sample-error-list">
                    <span class="error-tag">{{ sample.errorCode || '-' }}</span>
                    <span class="error-tag">{{ sample.errorMessage || '-' }}</span>
                  </div>
                </div>
                <div class="sample-block sample-actions">
                  <button class="btn btn-default btn-sm" @click="openEvidenceChainFromFailedSample(sample)">
                    <i class="h-icon-eye"></i>
                    查看证据链
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div v-else-if="failedSampleStage" class="evidence-sample-body">
            <div class="sample-summary">
              <div class="sample-chip">stage: {{ failedSampleStage.snapshotStage || '-' }}</div>
              <div class="sample-chip">postingStatus: {{ failedSampleStage.postingStatus || '-' }}</div>
              <div class="sample-chip">voucherId: {{ failedSampleStage.voucherId || '-' }}</div>
              <div class="sample-chip">traceId: {{ failedSampleStage.traceId || evidenceQueryState.traceId || '-' }}</div>
            </div>
            <div class="sample-block">
              <div class="sample-title">decisionReason</div>
              <div class="sample-text">{{ failedSampleStage.decisionReason || '暂无' }}</div>
            </div>
            <div class="sample-block">
              <div class="sample-title">errors</div>
              <div v-if="failedSampleStage.errors && failedSampleStage.errors.length" class="sample-error-list">
                <span v-for="(error, errorIndex) in failedSampleStage.errors" :key="errorIndex" class="error-tag">
                  {{ formatEvidenceError(error) }}
                </span>
              </div>
              <div v-else class="sample-empty">暂无错误信息</div>
            </div>
            <div class="sample-block">
              <div class="sample-title">postingResult</div>
              <pre>{{ formatMaskedJson(failedSampleStage.postingResult) }}</pre>
            </div>
          </div>
          <div v-else class="evidence-empty-state">
            <i class="h-icon-document"></i>
            <div class="empty-title">暂无失败样本</div>
            <div class="empty-desc">当前没有可查看的 POST_FAILED 记录。</div>
          </div>
        </div>
      </div>
    </div>
  </app-content>
</template>

<script>
export default {
  name: 'BusinessVoucher',
  data() {
    return {
      currentStep: 1,
      uploading: false,
      generating: false,
      selectedFile: null,
      fileName: '',
      formData: {
        businessType: 'shop_payment',  // 业务类型：shop_payment 或 inventory
        platform: 'amazon',
        storeCode: '',
        storeName: '',
        businessDate: this.getTodayDate()
      },
      taskDetail: null,
      voucherPreview: [],
      generateResult: null,
      currencies: [],
      subjectList: [],
      evidenceQuery: {
        voucherId: '',
        traceId: ''
      },
      evidenceQueryState: {
        voucherId: '',
        traceId: ''
      },
      evidenceDialogVisible: false,
      evidenceLoading: false,
      evidenceChain: {
        stages: []
      },
      evidenceChainMeta: {},
      evidenceChainError: '',
      memoryReviewViews: {},
      memoryReviewDiffs: {},
      pendingMemoryReviewActions: {},
      memoryReviewError: '',
      failedSampleQuery: {
        startDate: this.getDateOffset(-30),
        endDate: this.getTodayDate(),
        eventType: '',
        errorCode: ''
      },
      failedSampleLoading: false,
      failedSampleError: '',
      failedSampleList: [],
      failedSampleMode: 'detail',
      failedSampleDialogVisible: false,
      failedSampleStage: null
    };
  },
  async created() {
    await Promise.all([this.loadCurrencies(), this.loadSubjects()]);
    await this.bootstrapTaskFromRoute();
    this.bootstrapEvidenceChainFromRoute();
  },
  beforeDestroy() {
    this.detachOverlay('evidenceChainOverlay');
    this.detachOverlay('failedSampleOverlay');
  },
  computed: {
    // 是否有正在编辑的凭证
    hasEditingVoucher() {
      return this.voucherPreview.some(v => v.isEditing);
    },
    hasIncompleteVoucher() {
      return this.voucherPreview.some(v =>
        (v.entries || []).some(e =>
          (Number(e.debit) > 0 || Number(e.credit) > 0) && (!e.subjectCode || !e.subjectName)
        )
      );
    },
    // 待处理数量
    pendingCount() {
      return this.voucherPreview.filter(v => v.status === 'pending').length;
    },
    // 已确认数量
    confirmedCount() {
      return this.voucherPreview.filter(v => v.status === 'confirmed').length;
    },
    // 已删除数量
    deletedCount() {
      const total = (this.taskDetail && this.taskDetail.detailCount) || 0;
      return total - this.voucherPreview.length;
    },
    // 是否全部处理完毕（待处理为0）
    allProcessed() {
      return this.pendingCount === 0;
    },
    canGenerate() {
      return !this.generating && !this.hasEditingVoucher && this.pendingCount === 0 && this.confirmedCount > 0;
    },
    // 业务类型标签
    businessTypeLabel() {
      if (this.formData.businessType === 'inventory') {
        return '进销存';
      } else if (this.formData.businessType === 'shop_collection') {
        return '店铺回款';
      } else if (this.formData.businessType === 'fund_flow') {
        return '资金流水';
      } else {
        return '店铺营收';
      }
    },
    evidenceChainStages() {
      return (this.evidenceChain && this.evidenceChain.stages) || [];
    }
  },
  watch: {
    evidenceDialogVisible(visible) {
      if (visible) {
        this.$nextTick(() => this.mountOverlayToBody('evidenceChainOverlay'));
      }
    },
    failedSampleDialogVisible(visible) {
      if (visible) {
        this.$nextTick(() => this.mountOverlayToBody('failedSampleOverlay'));
      }
    }
  },
  methods: {
    mountOverlayToBody(refName) {
      const overlay = this.$refs[refName];
      if (!overlay || typeof document === 'undefined') {
        return;
      }
      if (overlay.parentNode !== document.body) {
        document.body.appendChild(overlay);
      }
    },

    detachOverlay(refName) {
      const overlay = this.$refs[refName];
      if (!overlay || typeof document === 'undefined') {
        return;
      }
      if (overlay.parentNode === document.body) {
        document.body.removeChild(overlay);
      }
    },

    // 获取今天日期
    getTodayDate() {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },

    getDateOffset(offsetDays) {
      const date = new Date();
      date.setDate(date.getDate() + Number(offsetDays || 0));
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },

    bootstrapEvidenceChainFromRoute() {
      const query = (this.$route && this.$route.query) || {};
      const voucherId = query.evidenceVoucherId || query.voucherId || '';
      const traceId = query.evidenceTraceId || query.traceId || '';
      if (!voucherId && !traceId) {
        return;
      }
      this.evidenceQuery.voucherId = voucherId ? String(voucherId) : '';
      this.evidenceQuery.traceId = traceId ? String(traceId) : '';
      this.openEvidenceChainByQuery();
    },

    async bootstrapTaskFromRoute() {
      const query = (this.$route && this.$route.query) || {};
      if (!query.taskId) {
        return;
      }
      try {
        const response = await this.$api.businessVoucher.getTaskDetail(query.taskId);
        if (!(response && response.code === 200 && response.data && response.data.task)) {
          this.$Message.error((response && response.msg) || '加载任务失败');
          return;
        }
        this.taskDetail = this.normalizeTaskDetail(response.data);
        await this.loadPreview();
        this.currentStep = 2;
      } catch (error) {
        console.error('加载任务失败', error);
        this.$Message.error((error && error.message) || '加载任务失败');
      }
    },

    normalizeTaskDetail(payload) {
      const task = (payload && payload.task) || {};
      let parseResult = {};
      if (task.parseResult) {
        try {
          parseResult = typeof task.parseResult === 'string' ? JSON.parse(task.parseResult) : task.parseResult;
        } catch (e) {
          parseResult = {};
        }
      }
      return {
        taskId: task.id,
        taskNo: task.taskNo,
        detailCount: task.detailCount || ((payload && payload.details && payload.details.length) || 0),
        totalAmount: task.totalAmount,
        unmatchedRowCount: parseResult.unmatchedRowCount || 0,
        totalRowCount: parseResult.totalRowCount || task.detailCount || 0,
        skippedRowCount: parseResult.skippedRowCount || 0,
        task,
        details: (payload && payload.details) || []
      };
    },

    fillEvidenceQueryFromCurrent() {
      const current = this.voucherPreview.find(v => this.isBankFlowV1Voucher(v)) || this.voucherPreview[0];
      if (!current) {
        this.$Message.info('当前没有可用样本，请先上传并解析业务数据');
        return;
      }
      const query = this.resolveEvidenceQueryFromVoucher(current);
      this.evidenceQuery.voucherId = query.voucherId || '';
      this.evidenceQuery.traceId = query.traceId || '';
      this.$Message.success('已填充当前样本的查询条件');
    },

    resolveEvidenceQueryFromVoucher(voucher) {
      if (!voucher) {
        return {
          voucherId: '',
          traceId: ''
        };
      }
      const bankFlowV1 = voucher.bankFlowV1 || {};
      const draft = bankFlowV1.voucherDraft || {};
      return {
        voucherId: voucher.voucherId || draft.voucherId || bankFlowV1.voucherId || voucher.id || voucher.detailId || '',
        traceId: voucher.traceId || bankFlowV1.traceId || draft.traceId || draft.snapshotTraceId || draft.sourceTraceId || ''
      };
    },

    async openEvidenceChainByVoucher(voucher) {
      const query = this.resolveEvidenceQueryFromVoucher(voucher);
      if (!query.voucherId && !query.traceId) {
        this.$Message.warning('当前样本没有可用的 voucherId 或 traceId');
        return;
      }
      this.evidenceQuery.voucherId = query.voucherId ? String(query.voucherId) : '';
      this.evidenceQuery.traceId = query.traceId ? String(query.traceId) : '';
      await this.openEvidenceChainByQuery();
    },

    async openEvidenceChainByQuery() {
      const voucherId = String(this.evidenceQuery.voucherId || '').trim();
      const traceId = String(this.evidenceQuery.traceId || '').trim();
      if (!voucherId && !traceId) {
        this.$Message.warning('请输入 voucherId 或 traceId');
        return;
      }
      await this.loadEvidenceChain({
        voucherId,
        traceId
      });
      if (!this.evidenceChainError) {
        this.evidenceDialogVisible = true;
      }
    },

    async loadEvidenceChain(params) {
      this.evidenceLoading = true;
      this.evidenceDialogVisible = true;
      this.evidenceChainError = '';
      this.evidenceChain = {
        stages: []
      };
      this.evidenceChainMeta = {};
      this.evidenceQueryState = {
        voucherId: params.voucherId || '',
        traceId: params.traceId || ''
      };
      try {
        const response = await this.$api.businessVoucher.getEvidenceChain(params);
        if (response && response.code === 200) {
          const normalized = this.normalizeEvidenceChainResponse(response.data);
          this.evidenceChain = normalized;
          this.evidenceChainMeta = normalized.meta || {};
          this.evidenceChainError = '';
        } else {
          this.evidenceChainError = (response && response.msg) || '加载证据链失败';
        }
      } catch (error) {
        console.error('加载证据链失败', error);
        this.evidenceChainError = (error && error.message) || '加载证据链失败';
      } finally {
        this.evidenceLoading = false;
      }
    },

    /** Load Decision Memory review view for a bank-flow voucher */
    async loadMemoryReviewView(voucher) {
      const detailId = voucher && voucher.detailId;
      if (!detailId) return;
      try {
        const response = await this.$api.businessVoucher.getMemoryReviewView(detailId);
        if (response && response.code === 200 && response.data) {
          this.$set(this.memoryReviewViews, voucher.voucherKey || String(detailId), response.data);
        }
      } catch (e) {
        this.memoryReviewError = (e && e.msg) || '加载记忆审核视图失败';
      }
    },

    handleMemoryReviewError(msg) {
      this.memoryReviewError = msg || '记忆审核操作失败';
      this.$Message.error(this.memoryReviewError);
    },

    /** Handle MODIFY_MEMORY_SUGGESTION: enter voucher editing state for the given detail */
    handleMemoryReviewModify(payload) {
      const voucher = this.voucherPreview.find(function(v) {
        return v.detailId === payload.detailId;
      });
      if (!voucher) {
        this.$Message.error('未找到对应业务明细');
        return;
      }
      // Snapshot current entries as system suggested lines (before user edits)
      voucher._systemSuggestedLines = voucher.entries.map((e) => {
        return {
          entryNo: e.entryNo,
          side: e.side,
          subjectCode: e.subjectCode,
          subjectName: e.subjectName,
          amount: Number(e.debit) || Number(e.credit) || 0,
          currency: e.currencyName || '',
          auxiliary: this.formatAuxiliary(e.selectedAuxiliary),
          lineReason: e.lineReason || '',
          source: 'SYSTEM_REUSED'
        };
      });
      // Track pending MODIFY action context
      var pending = {
        actionCode: 'MODIFY_MEMORY_SUGGESTION',
        reuseMode: payload.view && payload.view.reuseMode,
        matchedPatternId: payload.view && payload.view.matchedPatternId
      };
      this.$set(this.pendingMemoryReviewActions, payload.detailId, pending);
      // Clear any previous diffs for this detailId
      this.$delete(this.memoryReviewDiffs, payload.detailId);
      // Set the voucher to editing state so user can modify subjects/amounts
      this.$set(voucher, 'isEditing', true);
      this.$Message.info('已进入编辑模式，请修改分录后提交');
    },

    normalizeEvidenceChainResponse(payload) {
      const raw = payload && payload.data ? payload.data : payload;
      const meta = raw && !Array.isArray(raw) ? this.pickEvidenceMeta(raw) : {};
      const stageSource = this.extractEvidenceStages(raw);
      const stages = stageSource.map((stage, index) => this.normalizeEvidenceStage(stage, index));
      return {
        meta: Object.assign({}, meta, {
          resultCount: stages.length
        }),
        stages
      };
    },

    extractEvidenceStages(raw) {
      if (Array.isArray(raw)) {
        return raw;
      }
      if (!raw || typeof raw !== 'object') {
        return [];
      }
      const candidates = [
        raw.snapshots,
        raw.stages,
        raw.items,
        raw.records,
        raw.chain,
        raw.evidenceChain,
        raw.data
      ];
      for (let i = 0; i < candidates.length; i += 1) {
        if (Array.isArray(candidates[i])) {
          return candidates[i];
        }
      }
      if (raw.stage) {
        return [raw.stage];
      }
      return [];
    },

    pickEvidenceMeta(raw) {
      if (!raw || Array.isArray(raw) || typeof raw !== 'object') {
        return {};
      }
      const meta = {};
      ['voucherId', 'traceId', 'traceNo', 'bizType', 'accountSetsId', 'entityId', 'entityName', 'status', 'postingStatus', 'updatedAt', 'createdAt'].forEach(key => {
        if (raw[key] !== undefined && raw[key] !== null && raw[key] !== '') {
          meta[key] = raw[key];
        }
      });
      return meta;
    },

    normalizeEvidenceStage(stage, index) {
      const normalized = stage && typeof stage === 'object' ? Object.assign({}, stage) : {};
      const snapshotPayload = (normalized.snapshots && typeof normalized.snapshots === 'object') ? normalized.snapshots : {};
      const sourceRecord = this.extractEvidenceSection(normalized, ['sourceRecord', 'sourceRecordSnapshot', 'source']) || snapshotPayload.sourceRecord || null;
      const businessEvidence = this.extractEvidenceSection(normalized, ['businessEvidence', 'businessEvidenceSnapshot', 'evidence']) || snapshotPayload.businessEvidence || null;
      const relationContext = this.extractEvidenceSection(normalized, ['relationContext', 'relation', 'context']) || snapshotPayload.relationContext || null;
      const accountingEvent = this.extractEvidenceSection(normalized, ['accountingEvent', 'accountingEventSnapshot', 'eventSnapshot']) || snapshotPayload.accountingEvent || null;
      const accountingDecision = this.extractEvidenceSection(normalized, ['accountingDecision', 'decision', 'accountingDecisionSnapshot']) || snapshotPayload.accountingDecision || null;
      const voucherDraft = this.extractEvidenceSection(normalized, ['voucherDraft', 'draft', 'voucherDraftSnapshot']) || snapshotPayload.voucherDraft || null;
      const reviewFeedback = this.extractEvidenceSection(normalized, ['reviewFeedback', 'feedback', 'reviewFeedbackSnapshot']) || snapshotPayload.reviewFeedback || null;
      const postingResult = this.extractEvidenceSection(normalized, ['postingResult', 'posting', 'postingResultSnapshot']) || snapshotPayload.postingResult || null;
      const referenceFactContext = this.extractEvidenceSection(normalized, ['referenceFactContext', 'referenceFact', 'referenceFactContextSnapshot']) || snapshotPayload.referenceFactContext || null;
      const snapshotStage = normalized.snapshotStage || normalized.stage || normalized.stageName || normalized.phase || 'UNKNOWN';
      const postingStatus = normalized.postingStatus || normalized.postStatus || normalized.status || '';
      const stageClass = String(snapshotStage || '')
        .toLowerCase()
        .replace(/[^a-z0-9_-]/g, '_');
      return Object.assign(normalized, {
        _stageKey: normalized.id || normalized.snapshotId || normalized.traceId || `${snapshotStage}-${index}`,
        snapshotStage,
        snapshotStageClass: `stage-${stageClass}`,
        snapshotSequence: normalized.snapshotSequence !== undefined && normalized.snapshotSequence !== null ? normalized.snapshotSequence : index + 1,
        createdAt: normalized.createdAt || normalized.createdTime || normalized.snapshotCreatedAt || '',
        eventType: normalized.eventType || normalized.event || normalized.actionType || this.getNestedEvidenceValue(accountingEvent, ['data', 'eventType']) || '',
        decisionReason: normalized.decisionReason || normalized.reason || this.getNestedEvidenceValue(accountingDecision, ['data', 'decisionReason']) || '',
        draftStatus: normalized.draftStatus || this.getNestedEvidenceValue(voucherDraft, ['data', 'draftStatus']) || '',
        reviewAction: normalized.reviewAction || this.getNestedEvidenceValue(reviewFeedback, ['data', 'reviewAction']) || '',
        postingStatus,
        voucherId: normalized.voucherId || normalized.finalVoucherId || normalized.voucherDraftId || this.getNestedEvidenceValue(postingResult, ['data', 'voucherId']) || '',
        voucherWord: normalized.voucherWord || normalized.word || '',
        voucherCode: normalized.voucherCode || normalized.code || '',
        errors: this.normalizeEvidenceErrors(normalized.errors),
        sourceRecord,
        businessEvidence,
        relationContext,
        accountingEvent,
        accountingDecision,
        voucherDraft,
        reviewFeedback,
        postingResult,
        referenceFactContext,
        expanded: normalized.expanded !== undefined ? !!normalized.expanded : index === 0 || postingStatus === 'POST_FAILED' || snapshotStage === 'POST_FAILED'
      });
    },

    getNestedEvidenceValue(source, path) {
      if (!source || !path || !path.length) {
        return null;
      }
      let current = source;
      for (let i = 0; i < path.length; i += 1) {
        if (!current || typeof current !== 'object') {
          return null;
        }
        current = current[path[i]];
      }
      return current;
    },

    extractEvidenceSection(stage, keys) {
      for (let i = 0; i < keys.length; i += 1) {
        const key = keys[i];
        if (stage[key] !== undefined && stage[key] !== null) {
          return stage[key];
        }
      }
      return null;
    },

    normalizeEvidenceErrors(errors) {
      if (!errors) {
        return [];
      }
      if (Array.isArray(errors)) {
        return errors.filter(item => item !== null && item !== undefined && item !== '');
      }
      if (typeof errors === 'string') {
        return errors ? [errors] : [];
      }
      if (typeof errors === 'object') {
        return Object.keys(errors).map(key => `${key}: ${this.stringifyEvidenceValue(errors[key])}`);
      }
      return [String(errors)];
    },

    toggleEvidenceStage(stage) {
      this.$set(stage, 'expanded', !stage.expanded);
    },

    closeEvidenceChainDialog() {
      this.evidenceDialogVisible = false;
      this.evidenceLoading = false;
      this.evidenceChainError = '';
      this.failedSampleMode = 'detail';
      this.failedSampleDialogVisible = false;
      this.failedSampleStage = null;
    },

    async openFailedSamplesQuery() {
      this.failedSampleMode = 'list';
      this.failedSampleDialogVisible = true;
      await this.queryFailedSamples();
    },

    async queryFailedSamples() {
      this.failedSampleLoading = true;
      this.failedSampleError = '';
      this.failedSampleList = [];
      try {
        const response = await this.$api.businessVoucher.getFailedEvidenceSamples({
          startDate: this.failedSampleQuery.startDate || undefined,
          endDate: this.failedSampleQuery.endDate || undefined,
          eventType: this.failedSampleQuery.eventType || undefined,
          errorCode: this.failedSampleQuery.errorCode || undefined
        });
        if (response && response.code === 200) {
          const data = Array.isArray(response.data) ? response.data : [];
          this.failedSampleList = data.map((item, index) => Object.assign({}, item, {
            _sampleKey: `${item.traceId || 'trace'}-${item.createdAt || index}-${index}`
          }));
        } else {
          this.failedSampleError = (response && (response.msg || response.message)) || '加载失败样本失败';
        }
      } catch (error) {
        console.error('加载失败样本失败', error);
        this.failedSampleError = (error && error.message) || '加载失败样本失败';
      } finally {
        this.failedSampleLoading = false;
      }
    },

    openFailedSample(stage) {
      this.failedSampleMode = 'detail';
      this.failedSampleStage = stage || null;
      this.failedSampleDialogVisible = true;
    },

    async openEvidenceChainFromFailedSample(sample) {
      this.failedSampleDialogVisible = false;
      this.failedSampleMode = 'detail';
      this.failedSampleStage = null;
      await this.loadEvidenceChain({
        voucherId: sample && sample.finalVoucherId ? String(sample.finalVoucherId) : '',
        traceId: sample && sample.traceId ? String(sample.traceId) : ''
      });
      if (!this.evidenceChainError) {
        this.evidenceDialogVisible = true;
      }
    },

    openFailedSampleByVoucher(voucher) {
      const bankFlowV1 = voucher && voucher.bankFlowV1 ? voucher.bankFlowV1 : {};
      const draft = bankFlowV1.voucherDraft || {};
      const query = this.resolveEvidenceQueryFromVoucher(voucher);
      const stage = {
        snapshotStage: 'POST_FAILED',
        postingStatus: 'POST_FAILED',
        voucherId: query.voucherId,
        traceId: query.traceId,
        decisionReason: draft.decisionReason || ((bankFlowV1.accountingDecision && bankFlowV1.accountingDecision.decisionReason) || 'POST_FAILED 样本'),
        errors: this.normalizeEvidenceErrors(draft.postingErrors || bankFlowV1.postingErrors || draft.errors),
        postingResult: bankFlowV1.postingResult || draft.postingResult || null
      };
      this.openFailedSample(stage);
    },

    closeFailedSampleDialog() {
      this.failedSampleMode = 'detail';
      this.failedSampleDialogVisible = false;
      this.failedSampleStage = null;
    },

    hasPostingFailedStage(voucher) {
      if (!voucher) {
        return false;
      }
      const bankFlowV1 = voucher.bankFlowV1 || {};
      const draft = bankFlowV1.voucherDraft || {};
      const postingStatus = String(bankFlowV1.postingStatus || draft.postingStatus || draft.status || '').toUpperCase();
      const draftStatus = String(draft.draftStatus || '').toUpperCase();
      return postingStatus === 'POST_FAILED' || draftStatus === 'POST_FAILED';
    },

    formatFactMatchStatus(status) {
      const map = {
        MATCHED: '已命中参考事实',
        PARTIAL: '参考事实部分匹配，需人工确认',
        CANDIDATE: '参考事实部分匹配，需人工确认',
        CONFLICT: '参考事实存在冲突，请核对原始业务表',
        NOT_FOUND: '暂无可用参考事实',
        NO_MATCH: '暂无可用参考事实'
      };
      return map[status] || status || '暂无参考事实';
    },

    formatMatchType(matchType) {
      const map = {
        EXACT: '精确匹配',
        PARTIAL: '部分匹配',
        CONFLICT: '冲突',
        NO_MATCH: '未匹配'
      };
      return map[matchType] || matchType || '-';
    },

    formatJsonValue(value) {
      if (!value) return '-';
      if (typeof value === 'string') {
        try {
          const parsed = JSON.parse(value);
          return typeof parsed === 'object' ? JSON.stringify(parsed) : String(value);
        } catch (e) {
          return value;
        }
      }
      if (typeof value === 'object') {
        try {
          return JSON.stringify(value);
        } catch (e) {
          return String(value);
        }
      }
      return String(value);
    },

    formatDateTime(date) {
      if (!date) return '-';
      const d = new Date(date);
      if (Number.isNaN(d.getTime())) {
        return String(date);
      }
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      const hour = String(d.getHours()).padStart(2, '0');
      const minute = String(d.getMinutes()).padStart(2, '0');
      const second = String(d.getSeconds()).padStart(2, '0');
      return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
    },

    stringifyEvidenceValue(value) {
      if (value === null || value === undefined) {
        return '';
      }
      if (typeof value === 'string') {
        return value;
      }
      if (typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
      }
      try {
        return JSON.stringify(value);
      } catch (error) {
        return String(value);
      }
    },

    maskEvidenceString(text) {
      const value = String(text);
      if (!value) {
        return value;
      }
      if (/^\d+$/.test(value) && value.length > 6) {
        return `${value.slice(0, 3)}****${value.slice(-3)}`;
      }
      if (value.length <= 2) {
        return `${value[0] || ''}*`;
      }
      if (value.length <= 6) {
        return `${value.slice(0, 1)}**${value.slice(-1)}`;
      }
      if (value.length <= 12) {
        return `${value.slice(0, 2)}****${value.slice(-2)}`;
      }
      return `${value.slice(0, 4)}****${value.slice(-4)}`;
    },

    maskEvidenceObject(value, depth, keyHint) {
      if (value === null || value === undefined) {
        return value;
      }
      if (depth > 5) {
        return this.stringifyEvidenceValue(value);
      }
      if (typeof value === 'string') {
        const keyName = String(keyHint || '').toLowerCase();
        if (!keyName) {
          return value;
        }
        if (/(bankaccount|counterpartyaccount|accountno|accountnumber|bank_account|counterparty_account|account_no|account_number)/.test(keyName)) {
          return this.maskEvidenceString(value);
        }
        return value;
      }
      if (typeof value === 'number' || typeof value === 'boolean') {
        return value;
      }
      if (Array.isArray(value)) {
        return value.map(item => this.maskEvidenceObject(item, depth + 1, keyHint));
      }
      const result = {};
      Object.keys(value).forEach(key => {
        result[key] = this.maskEvidenceObject(value[key], depth + 1, key);
      });
      return result;
    },

    formatMaskedJson(value) {
      if (value === null || value === undefined || value === '') {
        return '暂无数据';
      }
      try {
        return JSON.stringify(this.maskEvidenceObject(value, 0), null, 2);
      } catch (error) {
        return this.maskEvidenceString(this.stringifyEvidenceValue(value));
      }
    },

    formatEvidenceError(error) {
      if (error === null || error === undefined || error === '') {
        return '-';
      }
      if (typeof error === 'string') {
        return error;
      }
      if (typeof error === 'object') {
        const code = error.code ? String(error.code) : '';
        const message = error.message ? String(error.message) : '';
        if (code && message) {
          return `${code}: ${message}`;
        }
        return code || message || this.stringifyEvidenceValue(error);
      }
      return String(error);
    },

    // 开始编辑
    startEdit(voucher) {
      voucher._backup = JSON.parse(JSON.stringify({
        summary: voucher.summary,
        entries: voucher.entries,
        changeReason: voucher.changeReason,
        finalDecision: voucher.finalDecision
      }));
      // 初始化每行的科目对象、外币字段
      voucher.entries.forEach((entry, entryIndex) => {
        if (!entry.subject && entry.subjectCode) {
          entry.subject = this.subjectList.find(s => s.code === entry.subjectCode) || null;
        }
        if (entry.currencyId == null) entry.currencyId = null;
        if (!entry.originalAmount) entry.originalAmount = '';
        if (!entry.exchangeRate) entry.exchangeRate = '';
        if (!entry.selectedAuxiliary || typeof entry.selectedAuxiliary !== 'object') {
          this.$set(entry, 'selectedAuxiliary', {});
        }
        if (this.isBankFlowV1Voucher(voucher)) {
          entry.entryNo = entry.entryNo || entryIndex + 1;
        }
        entry._voucherRef = voucher;
        entry._subjectDropdown = [];
      });
      voucher.isEditing = true;
      voucher.isBalanced = this.checkBalance(voucher);
      this.$forceUpdate();
    },

    // 保存编辑
    async saveEdit(voucher) {
      if (!this.checkBalance(voucher)) {
        this.$Message.error('借贷不平衡，请检查金额');
        return;
      }

      // 过滤掉完全空白的行（科目和金额都为空）
      const validEntries = voucher.entries.filter(e =>
        e.subjectCode || e.subjectName || Number(e.debit) > 0 || Number(e.credit) > 0
      );

      const emptySubject = validEntries.find(e => !e.subjectCode || !e.subjectName);
      if (emptySubject) {
        this.$Message.error('请填写完整的科目代码和名称');
        return;
      }

      voucher.entries = validEntries;

      if (voucher.detailId) {
        try {
          // 构建 customEntries（支持任意行数和外币）
          const customEntries = voucher.entries.map(e => ({
            subjectCode: (e.subjectCode || '').trim(),
            subjectName: (e.subjectName || '').trim(),
            debitAmount: Number(e.debit) || 0,
            creditAmount: Number(e.credit) || 0,
            currencyId: e.currencyId || null,
            currencyName: e.currencyName || '',
            originalAmount: e.originalAmount ? Number(e.originalAmount) : null,
            exchangeRate: e.exchangeRate ? Number(e.exchangeRate) : null
          }));

          const detailUpdate = {
            id: voucher.detailId,
            summary: voucher.summary,
            customEntries
          };

          // 同时更新兼容字段（借方+第一贷方+第二贷方）
          const debitEntry = voucher.entries.find(e => Number(e.debit) > 0);
          const creditEntries = voucher.entries.filter(e => Number(e.credit) > 0);
          if (debitEntry) {
            detailUpdate.debitSubjectCode = debitEntry.subjectCode;
            detailUpdate.debitSubjectName = debitEntry.subjectName;
            detailUpdate.amount = Number(debitEntry.debit);
          }
          if (creditEntries[0]) {
            detailUpdate.creditSubjectCode = creditEntries[0].subjectCode;
            detailUpdate.creditSubjectName = creditEntries[0].subjectName;
            detailUpdate.creditAmount1 = Number(creditEntries[0].credit);
            if (!detailUpdate.amount) detailUpdate.amount = Number(creditEntries[0].credit);
          }
          if (creditEntries[1]) {
            detailUpdate.secondCreditSubjectCode = creditEntries[1].subjectCode;
            detailUpdate.secondCreditSubjectName = creditEntries[1].subjectName;
            detailUpdate.creditAmount2 = Number(creditEntries[1].credit);
          }

          if (this.isBankFlowV1Voucher(voucher)) {
            detailUpdate.reviewAction = 'APPROVE';
            detailUpdate.draftEntries = this.buildReviewDraftEntries(voucher);
            detailUpdate.changeReason = voucher.changeReason || '人工确认科目与辅助核算';
            detailUpdate.finalDecision = voucher.finalDecision || '确认生成银行流水凭证草稿';
            detailUpdate.feedbackTags = Array.isArray(voucher.feedbackTags) ? voucher.feedbackTags : [];
          }

          await this.$api.businessVoucher.updateDetails([detailUpdate]);

          // Submit MODIFY_MEMORY_SUGGESTION only when triggered by the MODIFY button
          if (this.isBankFlowV1Voucher(voucher) && voucher.detailId) {
            var pendingAction = this.pendingMemoryReviewActions[voucher.detailId];
            if (pendingAction && pendingAction.actionCode === 'MODIFY_MEMORY_SUGGESTION') {
              const memoryView = this.memoryReviewViews[voucher.voucherKey || String(voucher.detailId)];
              try {
                var systemSuggestedLines = voucher._systemSuggestedLines || [];
                var reviewedLines = voucher.entries.map((e) => {
                  return {
                    entryNo: e.entryNo,
                    side: e.side,
                    subjectCode: e.subjectCode,
                    subjectName: e.subjectName,
                    amount: Number(e.debit) || Number(e.credit) || 0,
                    currency: e.currencyName || '',
                    auxiliary: this.formatAuxiliary(e.selectedAuxiliary),
                    lineReason: e.lineReason || '',
                    source: 'HUMAN_MODIFIED'
                  };
                });
                var modifyPayload = {
                  actionCode: 'MODIFY_MEMORY_SUGGESTION',
                  reuseMode: pendingAction.reuseMode || (memoryView && memoryView.reuseMode),
                  reviewComment: detailUpdate.changeReason || '人工修改分录',
                  traceId: memoryView && memoryView.currentEventSnapshot ? memoryView.currentEventSnapshot.traceId : null,
                  systemSuggestedLines: systemSuggestedLines,
                  reviewedLines: reviewedLines
                };
                var modifyResult = await this.$api.businessVoucher.submitMemoryReviewAction(voucher.detailId, modifyPayload);
                // Store enriched diff state: lineDiffs + full-line tables for visualization
                var diffKey = voucher.voucherKey || String(voucher.detailId);
                var diffState = {
                  lineDiffs: modifyResult && modifyResult.code === 200 && modifyResult.data ? modifyResult.data.lineDiffs : [],
                  systemSuggestedLines: systemSuggestedLines,
                  reviewedLines: reviewedLines,
                  hasDiffs: modifyResult && modifyResult.code === 200 && modifyResult.data && modifyResult.data.lineDiffs && modifyResult.data.lineDiffs.length > 0
                };
                this.$set(this.memoryReviewDiffs, diffKey, diffState);
                this.loadMemoryReviewView(voucher);
              } catch (e) {
                console.warn('Memory review action submission failed (non-blocking):', e);
              }
            }
            // Clear pending MODIFY state regardless of trigger source
            delete voucher._systemSuggestedLines;
            this.$delete(this.pendingMemoryReviewActions, voucher.detailId);
          }

          if (this.isBankFlowV1Voucher(voucher)) {
            await this.loadPreview();
            const refreshedVoucher = this.voucherPreview.find(item => item.detailId === voucher.detailId);
            if (refreshedVoucher && refreshedVoucher.bankFlowV1 && refreshedVoucher.bankFlowV1.voucherDraft.draftStatus === 'APPROVED') {
              this.$Message.success('草稿已通过审核');
            } else {
              this.$Message.warning('审核结果已提交，但草稿尚未通过硬校验');
            }
            return;
          }
        } catch (error) {
          console.error('保存编辑到后端失败', error);
          this.$Message.warning('编辑已保存到本地，但同步到服务器失败');
        }
      }

      voucher.needsReview = voucher.entries.some(e => !e.subjectCode || !e.subjectName);
      voucher.status = 'confirmed';
      voucher.isEditing = false;
      delete voucher._backup;
      this.$Message.success('保存成功');
      this.$forceUpdate();
    },

    // 取消编辑
    cancelEdit(voucher) {
      if (voucher._backup) {
        voucher.summary = voucher._backup.summary;
        voucher.entries = voucher._backup.entries;
        voucher.changeReason = voucher._backup.changeReason;
        voucher.finalDecision = voucher._backup.finalDecision;
        delete voucher._backup;
      }
      // Clear pending memory review state
      delete voucher._systemSuggestedLines;
      if (voucher.detailId) {
        this.$delete(this.pendingMemoryReviewActions, voucher.detailId);
      }
      voucher.isEditing = false;
      this.$forceUpdate();
    },

    // 验证借贷平衡
    validateBalance(voucher) {
      voucher.isBalanced = this.checkBalance(voucher);
      this.$forceUpdate();
    },

    // 检查借贷平衡
    checkBalance(voucher) {
      const debitTotal = this.calculateDebitTotal(voucher);
      const creditTotal = this.calculateCreditTotal(voucher);
      return Math.abs(debitTotal - creditTotal) < 0.01;
    },

    findExchangeGainLossEntry(voucher) {
      return (voucher.entries || []).find(entry =>
        String(entry.subjectCode || '').trim() === '6061' ||
        String(entry.subjectName || '').includes('汇兑损益')
      );
    },

    rebalanceExchangeGainLoss(voucher) {
      const gainLossEntry = this.findExchangeGainLossEntry(voucher);
      if (!gainLossEntry) return;

      let debitWithoutGainLoss = 0;
      let creditWithoutGainLoss = 0;
      (voucher.entries || []).forEach(entry => {
        if (entry === gainLossEntry) return;
        debitWithoutGainLoss += Number(entry.debit) || 0;
        creditWithoutGainLoss += Number(entry.credit) || 0;
      });

      const diff = Number((debitWithoutGainLoss - creditWithoutGainLoss).toFixed(2));
      gainLossEntry.currencyId = null;
      gainLossEntry.currencyName = '';
      gainLossEntry.originalAmount = '';
      gainLossEntry.exchangeRate = '';

      if (Math.abs(diff) < 0.01) {
        gainLossEntry.debit = 0;
        gainLossEntry.credit = 0;
        gainLossEntry.entryType = 'credit';
        return;
      }

      if (diff > 0) {
        gainLossEntry.debit = 0;
        gainLossEntry.credit = diff.toFixed(2);
        gainLossEntry.entryType = 'credit';
      } else {
        gainLossEntry.debit = Math.abs(diff).toFixed(2);
        gainLossEntry.credit = 0;
        gainLossEntry.entryType = 'debit';
      }
    },

    // 计算借方合计
    calculateDebitTotal(voucher) {
      return voucher.entries.reduce((sum, entry) => sum + (Number(entry.debit) || 0), 0);
    },

    // 计算贷方合计
    calculateCreditTotal(voucher) {
      return voucher.entries.reduce((sum, entry) => sum + (Number(entry.credit) || 0), 0);
    },

    // 加载币种列表
    async loadCurrencies() {
      try {
        const res = await this.$api.setting.currency.list();
        this.currencies = (res && res.data) || [];
      } catch (e) { /* ignore */ }
    },

    // 加载科目列表
    async loadSubjects() {
      try {
        const res = await this.$api.setting.subject.voucherSelect();
        this.subjectList = (res && res.data) || [];
      } catch (e) { /* ignore */ }
    },

    // 解析科目支持的外币列表
    resolveSubjectCurrencies(entry) {
      const subject = entry && entry.subject;
      if (!subject || !subject.currencyAccounting) return [];
      try {
        let selected = JSON.parse(subject.currencyAccounting);
        if (Array.isArray(selected)) {
          const codes = selected.map(item => item.code || item.name || item).filter(Boolean);
          return this.currencies.filter(c => codes.includes(c.code));
        }
      } catch (e) { /* ignore */ }
      const codes = String(subject.currencyAccounting).split(',').map(s => s.trim()).filter(Boolean);
      return this.currencies.filter(c => codes.includes(c.code));
    },

    // 科目代码输入：过滤下拉
    onSubjectCodeInput(entry) {
      const q = (entry.subjectCode || '').trim();
      if (!q) { this.$set(entry, '_subjectDropdown', []); return; }
      const matches = this.subjectList.filter(s =>
        s.code.startsWith(q) || s.name.includes(q)
      ).slice(0, 10);
      this.$set(entry, '_subjectDropdown', matches);
    },

    // 失焦关闭下拉（延迟等待 mousedown 先触发）
    closeSubjectDropdown(entry) {
      setTimeout(() => { this.$set(entry, '_subjectDropdown', []); }, 200);
    },

    // 选择科目
    selectSubject(entry, subject) {
      entry.subjectCode = subject.code;
      entry.subjectName = subject.name;
      entry.subject = subject;
      this.$set(entry, '_subjectDropdown', []);
      if (!this.resolveSubjectCurrencies(entry).length) {
        entry.currencyId = null;
        entry.originalAmount = '';
        entry.exchangeRate = '';
      }
      this.$forceUpdate();
    },

    // 新增科目行
    addEntry(voucher) {
      const nextEntryNo = this.isBankFlowV1Voucher(voucher)
        ? Math.max(0, ...(voucher.entries || []).map(entry => Number(entry.entryNo) || 0)) + 1
        : null;
      voucher.entries.push({
        entryNo: nextEntryNo,
        subjectCode: '', subjectName: '', subject: null,
        entryType: 'credit', side: 'CREDIT', debit: 0, credit: 0,
        currencyId: null, currencyName: '', originalAmount: '', exchangeRate: '',
        auxiliaryRequirement: { required: false },
        selectedAuxiliary: {},
        _subjectDropdown: []
      });
      this.$forceUpdate();
    },

    // 删除科目行
    removeEntry(voucher, idx) {
      if (voucher.entries.length <= 1) {
        this.$Message.warning('至少保留一行');
        return;
      }
      voucher.entries.splice(idx, 1);
      voucher.isBalanced = this.checkBalance(voucher);
      this.$forceUpdate();
    },

    // 借方金额输入：互斥贷方
    onDebitInput(voucher, entry) {
      if (Number(entry.debit) > 0) {
        entry.credit = 0;
        entry.entryType = 'debit';
        entry.side = 'DEBIT';
      }
      this.validateBalance(voucher);
    },

    // 贷方金额输入：互斥借方
    onCreditInput(voucher, entry) {
      if (Number(entry.credit) > 0) {
        entry.debit = 0;
        entry.entryType = 'credit';
        entry.side = 'CREDIT';
      }
      this.validateBalance(voucher);
    },

    // 外币切换
    onCurrencyChange(entry) {
      if (!entry.currencyId) {
        entry.currencyName = '';
        entry.exchangeRate = '';
        entry.originalAmount = '';
        if (entry._voucherRef) {
          this.rebalanceExchangeGainLoss(entry._voucherRef);
          this.validateBalance(entry._voucherRef);
        }
        return;
      }
      const currency = this.currencies.find(c => c.id == entry.currencyId);
      if (currency) {
        entry.currencyName = currency.name;
        if (!entry.exchangeRate && currency.exchangeRate) {
          entry.exchangeRate = currency.exchangeRate;
        }
      }
      if (entry._voucherRef) {
        this.syncForeignAmount(entry._voucherRef, entry);
      }
      this.$forceUpdate();
    },

    // 原币/汇率变化 → 计算本币金额
    syncForeignAmount(voucher, entry) {
      if (!entry.currencyId || !entry.originalAmount || !entry.exchangeRate) return;
      const local = (Number(entry.originalAmount) * Number(entry.exchangeRate)).toFixed(2);
      if (entry.entryType === 'debit' || Number(entry.debit) > 0) {
        entry.debit = local;
        entry.credit = 0;
      } else {
        entry.credit = local;
        entry.debit = 0;
      }
      this.rebalanceExchangeGainLoss(voucher);
      this.validateBalance(voucher);
    },

    // 获取今日汇率
    async fetchCurrentRate(entry) {
      if (!entry.currencyId) { this.$Message.warning('请先选择币种'); return; }
      const currency = this.currencies.find(c => c.id == entry.currencyId);
      if (!currency) return;
      this.$set(entry, '_rateLoading', true);
      try {
        const res = await this.$api.setting.currency.publicRate({ code: currency.code });
        entry.exchangeRate = res.data.exchangeRate;
        if (entry._voucherRef) {
          this.syncForeignAmount(entry._voucherRef, entry);
        }
        this.$Message.success(`已更新${currency.code}今日汇率`);
      } catch (e) {
        this.$Message.warning('获取汇率失败');
      } finally {
        this.$set(entry, '_rateLoading', false);
      }
    },

    // 确认凭证（无需编辑时直接确认）
    async confirmVoucher(voucher) {
      if (this.isBankFlowV1Voucher(voucher)) {
        if (voucher.bankFlowV1 && voucher.bankFlowV1.voucherDraft && voucher.bankFlowV1.voucherDraft.draftStatus === 'APPROVED') {
          voucher.status = 'confirmed';
          this.$forceUpdate();
          return;
        }
        this.$Message.warning('该银行流水草稿需要先补全并提交审核');
        this.startEdit(voucher);
        return;
      }
      if (voucher.needsReview) {
        this.$Message.warning('该凭证有需要人工确认的内容，请先编辑补全');
        this.startEdit(voucher);
        return;
      }
      voucher.status = 'confirmed';
      this.$Message.success('已确认');
      this.$forceUpdate();
    },

    // 删除凭证（删除也是一种人工确认）
    async deleteVoucher(voucher, index) {
      try {
        await this.$Confirm('删除后该笔业务将不会生成凭证，确定删除吗？', '确认删除');
        // 后端标记删除
        await this.$api.businessVoucher.updateDetails([{
          id: voucher.detailId,
          deleted: true
        }]);
        // 前端移除
        this.voucherPreview.splice(index, 1);
        this.$Message.success('已删除');
      } catch (error) {
        if (error === 'cancel' || (error && error.message === 'cancel')) {
          return;
        }
        console.error('删除失败', error);
        this.$Message.error('删除失败，请重试');
      }
    },

    // 一键确认所有待处理凭证
    async confirmAllPending() {
      const pending = this.voucherPreview.filter(v => v.status === 'pending');
      if (pending.length === 0) {
        this.$Message.info('没有待确认的凭证');
        return;
      }
      if (pending.some(v => this.isBankFlowV1Voucher(v))) {
        this.$Message.warning('银行流水 V1 草稿暂不支持批量确认，请逐张补全并提交审核');
        return;
      }
      const hasReview = pending.some(v => v.needsReview);
      if (hasReview) {
        this.$Message.warning('存在需要编辑补全的凭证，无法批量确认，请先逐个处理');
        return;
      }
      pending.forEach(v => {
        v.status = 'confirmed';
      });
      this.$Message.success(`已确认 ${pending.length} 张凭证`);
      this.$forceUpdate();
    },

    // 文件选择
    handleFileChange(event) {
      const file = event.target.files[0];
      if (file) {
        // 验证文件类型
        const validTypes = ['application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
        if (!validTypes.includes(file.type) && !file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
          this.$Message.error('请选择Excel文件（.xlsx或.xls格式）');
          return;
        }

        this.selectedFile = file;
        this.fileName = file.name;
      }
    },

    // 上传并解析
    async handleUpload() {
      if (!this.selectedFile) {
        this.$Message.error('请选择文件');
        return;
      }

      if (!this.formData.businessDate) {
        this.$Message.error('请选择业务日期');
        return;
      }

      this.uploading = true;

      try {
        const formData = new FormData();
        formData.append('file', this.selectedFile);

        const response = await this.$api.businessVoucher.uploadBusinessVoucher(
          formData,
          this.formData.platform,
          this.formData.businessType,
          this.formData.storeCode,
          this.formData.storeName,
          this.formData.businessDate
        );

        if (response && response.code === 200) {
          this.taskDetail = response.data;
          this.$Message.success('文件解析成功');

          // 加载预览数据
          await this.loadPreview();

          // 进入下一步
          this.currentStep = 2;
        } else {
          this.$Message.error(response.msg || '上传失败');
        }
      } catch (error) {
        console.error('上传失败', error);
        this.$Message.error(error.message || '上传失败，请稍后重试');
      } finally {
        this.uploading = false;
      }
    },

    // 加载预览
    async loadPreview() {
      try {
        const response = await this.$api.businessVoucher.previewVouchers(this.taskDetail.taskId);
        if (response && response.code === 200) {
          this.voucherPreview = (response.data || []).map(v => this.normalizePreviewVoucher(v));
          // Preload Decision Memory review-views for bank-flow vouchers
          setTimeout(() => {
            this.voucherPreview.forEach(v => {
              if (this.isBankFlowV1Voucher(v) && v.detailId) {
                this.loadMemoryReviewView(v);
              }
            });
          }, 0);
        } else {
          this.$Message.error(response.msg || '加载预览失败');
        }
      } catch (error) {
        console.error('加载预览失败', error);
        this.$Message.error('加载预览失败');
      }
    },

    // 返回上一步
    handleBack() {
      this.currentStep = 1;
      // 重置文件
      this.selectedFile = null;
      this.fileName = '';
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },

    // 生成凭证
    async handleGenerate() {
      if (!this.canGenerate) {
        if (this.pendingCount > 0) {
          this.$Message.error(`还有 ${this.pendingCount} 张凭证未确认，请先确认或删除`);
        } else if (this.hasEditingVoucher) {
          this.$Message.error('仍有凭证处于编辑状态，请先保存或取消编辑');
        } else if (this.confirmedCount === 0) {
          this.$Message.error('没有可生成的已确认凭证');
        }
        return;
      }

      const confirmedVouchers = this.voucherPreview.filter(v => v.status === 'confirmed');
      const hasIncompleteConfirmedVoucher = confirmedVouchers.some(v =>
        (v.entries || []).some(e =>
          (Number(e.debit) > 0 || Number(e.credit) > 0) && (!e.subjectCode || !e.subjectName)
        )
      );

      if (hasIncompleteConfirmedVoucher) {
        this.$Message.error('仍有待人工确认的凭证未补全科目，请先完成编辑并保存');
        return;
      }

      this.generating = true;

      try {
        const response = await this.$api.businessVoucher.generateVouchers(this.taskDetail.taskId);

        if (response && response.code === 200) {
          this.generateResult = response.data;
          this.$Message.success('凭证生成成功！');
          this.currentStep = 3;
        } else {
          this.$Message.error(response.msg || '生成凭证失败');
        }
      } catch (error) {
        console.error('生成凭证失败', error);
        this.$Message.error(error.message || '生成凭证失败');
      } finally {
        this.generating = false;
      }
    },

    // 查看凭证列表
    goToVoucherList() {
      this.$router.push('/voucher');
    },

    // 重置
    handleReset() {
      this.currentStep = 1;
      this.selectedFile = null;
      this.fileName = '';
      this.taskDetail = null;
      this.voucherPreview = [];
      this.generateResult = null;
      // Clear memory review state
      this.memoryReviewViews = {};
      this.memoryReviewDiffs = {};
      this.pendingMemoryReviewActions = {};

      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },

    // 格式化日期
    formatDate(date) {
      if (!date) return '';
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },

    // 格式化金额
    formatMoney(amount) {
      if (!amount) return '0.00';
      return Number(amount).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    },

    formatLlmConfidence(value) {
      const num = typeof value === 'string' ? parseFloat(value) : value;
      if (Number.isNaN(num)) return '-';
      return (num * 100).toFixed(0) + '%';
    },

    formatLlmAdviceStatus(status) {
      const map = {
        SUCCESS: 'AI 草稿可用',
        FAILED_VALIDATION: '校验未通过',
        PARSE_FAILED: '解析失败',
        PARSE_ERROR: '解析失败',
        TIMEOUT: '调用超时',
        ERROR: '调用异常',
        PROVIDER_ERROR: '调用异常',
        DISABLED: '未启用',
        SKIPPED: '未生成'
      };
      return map[status] || status || '-';
    },

    llmDraftStatusLabel(advice) {
      if (!advice) return '-';
      if (advice.status === 'SUCCESS' && advice.adopted) {
        return (advice.riskWarnings && advice.riskWarnings.length) ? 'AI 草稿（请重点审核）' : 'AI 草稿（待审核）';
      }
      if (advice.status === 'FAILED_VALIDATION') return 'AI 草稿未通过安全校验，已自动退回';
      return 'AI 未给出草稿，请人工处理';
    },

    llmDraftStatusClass(advice) {
      if (!advice) return 'default';
      if (advice.status === 'SUCCESS' && advice.adopted) {
        return (advice.riskWarnings && advice.riskWarnings.length) ? 'warning' : 'success';
      }
      if (advice.status === 'FAILED_VALIDATION') return 'danger';
      return 'default';
    },

    formatLlmRiskWarning(code) {
      const map = {
        'NEGATIVE_ENDING_BALANCE': '应付账款期末余额为负，可能存在超付、预付款或退款，请核实。',
        'COUNTERPARTY_UNMATCHED': '对手方关系未匹配，暂不能确认其业务角色，请先完成关系匹配或人工判断。',
        'FACT_MISSING': '未找到足够的业务事实，暂不能生成可信草稿，请补充事实或人工处理。',
        'HAS_EXCESS_AMOUNT': '本次金额超过当前可解释金额，请核实是否存在超付、预付或往来调整。',
        'FX_PAIR_MISSING': '换汇流水缺少配对记录，请补充买入/卖出对应流水后再处理。',
        'RULE_CANDIDATES_UNAVAILABLE': '当前信息不足，AI 未生成草稿，请人工处理。',
      };
      return map[code] || code;
    },

    llmSkippedReason(advice, riskFlags) {
      if (!advice || advice.status === 'SUCCESS' || advice.status === 'FAILED_VALIDATION') return '';
      const allRisks = [...(advice.riskWarnings || []), ...(riskFlags || [])];
      if (allRisks.includes('COUNTERPARTY_UNMATCHED')) return '对手方关系未匹配，暂不能确认业务角色。';
      if (allRisks.includes('FACT_MISSING')) return '未找到足够的业务事实，暂不能生成可信草稿。';
      if (advice.errorMessage && advice.errorMessage.includes('rule candidates unavailable')) {
        return '当前缺少安全的科目候选，系统未让 AI 生成确定分录。';
      }
      return '当前信息不足，AI 未生成草稿，请人工处理。';
    },

    // Diff field label mapping for display
    diffFieldLabel(field) {
      var labels = {
        'subjectCode': '科目代码',
        'subjectName': '科目名称',
        'amount': '金额',
        'auxiliary': '辅助核算',
        'lineReason': '摘要',
        'currency': '币种',
        'ENTRY': '新增分录'
      };
      return labels[field] || field;
    },

    isBankFlowV1Voucher(voucher) {
      return !!(voucher && voucher.bankFlowV1 && voucher.bankFlowV1.voucherDraft);
    },

    normalizePreviewVoucher(voucher) {
      const bankFlowV1 = voucher.bankFlowV1 || null;
      const normalized = {
        ...voucher,
        status: 'pending',
        isEditing: false,
        changeReason: '',
        finalDecision: bankFlowV1 && bankFlowV1.reviewFeedback ? bankFlowV1.reviewFeedback.finalDecision || '' : '',
        feedbackTags: []
      };
      if (!bankFlowV1) {
        return normalized;
      }
      normalized.entries = this.mapDraftEntries(bankFlowV1.voucherDraft.entries || []);
      normalized.needsReview = bankFlowV1.voucherDraft.draftStatus !== 'APPROVED';
      normalized.status = bankFlowV1.voucherDraft.draftStatus === 'APPROVED' ? 'confirmed' : 'pending';
      return normalized;
    },

    mapDraftEntries(entries) {
      return (entries || []).map((entry, index) => ({
        entryNo: entry.entryNo || index + 1,
        entryType: entry.side === 'DEBIT' ? 'debit' : 'credit',
        side: entry.side,
        subjectCandidateType: entry.subjectCandidateType || [],
        subjectCode: entry.subjectCode || '',
        subjectName: entry.subjectName || '',
        subject: entry.subjectCode ? (this.subjectList.find(s => s.code === entry.subjectCode) || null) : null,
        debit: Number(entry.debitAmount || 0),
        credit: Number(entry.creditAmount || 0),
        currencyId: entry.currencyId || null,
        currencyName: entry.currencyName || '',
        originalAmount: entry.originalAmount || '',
        exchangeRate: entry.exchangeRate || '',
        amountSource: entry.amountSource || '',
        evidenceRef: entry.evidenceRef || '',
        auxiliaryRequirement: entry.auxiliaryRequirement || { required: false },
        selectedAuxiliary: entry.selectedAuxiliary ? { ...entry.selectedAuxiliary } : {},
        lineRiskFlags: entry.lineRiskFlags || [],
        lineReason: entry.lineReason || '',
        selectionSource: entry.selectionSource || 'UNSELECTED',
        _subjectDropdown: []
      }));
    },

    isEntryFieldEditable(voucher, entryIndex, fieldName) {
      return true;
    },

    buildReviewDraftEntries(voucher) {
      return (voucher.entries || []).map((entry, index) => ({
        entryNo: entry.entryNo || index + 1,
        side: Number(entry.debit) > 0 ? 'DEBIT' : 'CREDIT',
        subjectCode: (entry.subjectCode || '').trim(),
        subjectName: (entry.subjectName || '').trim(),
        selectionSource: 'HUMAN_CONFIRMED',
        selectedAuxiliary: this.normalizeSelectedAuxiliary(entry.selectedAuxiliary),
        debitAmount: Number(entry.debit) || 0,
        creditAmount: Number(entry.credit) || 0
      }));
    },

    normalizeSelectedAuxiliary(selectedAuxiliary) {
      if (!selectedAuxiliary || typeof selectedAuxiliary !== 'object') {
        return null;
      }
      const result = {
        selectionSource: 'HUMAN_CONFIRMED'
      };
      if (selectedAuxiliary.categoryId !== undefined && selectedAuxiliary.categoryId !== '') {
        result.categoryId = Number(selectedAuxiliary.categoryId);
      }
      if (selectedAuxiliary.detailId !== undefined && selectedAuxiliary.detailId !== '') {
        result.detailId = Number(selectedAuxiliary.detailId);
      }
      if (selectedAuxiliary.anchorEntityId) {
        result.anchorEntityId = selectedAuxiliary.anchorEntityId;
      }
      return Object.keys(result).length > 1 ? result : null;
    },

    formatAuxiliary(selectedAuxiliary) {
      if (!selectedAuxiliary || typeof selectedAuxiliary !== 'object') {
        return '';
      }
      const parts = [];
      if (selectedAuxiliary.categoryId !== undefined && selectedAuxiliary.categoryId !== '') {
        parts.push(`categoryId=${selectedAuxiliary.categoryId}`);
      }
      if (selectedAuxiliary.detailId !== undefined && selectedAuxiliary.detailId !== '') {
        parts.push(`detailId=${selectedAuxiliary.detailId}`);
      }
      if (selectedAuxiliary.anchorEntityId) {
        parts.push(`anchorEntityId=${selectedAuxiliary.anchorEntityId}`);
      }
      return parts.join(', ');
    }
  }
};
</script>

<style scoped lang="less">
.business-voucher-page {
  padding: 20px;
}

.evidence-query-bar {
  margin-bottom: 16px;
  padding: 14px 18px;
  background: linear-gradient(135deg, #f8fbff 0%, #eef6ff 100%);
  border: 1px solid #d9ecff;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.evidence-query-title {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #1f4f8f;
  font-weight: 600;
}

.evidence-query-form {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.failed-query-form,
.failed-query-inline {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  width: 100%;
}

.evidence-query-input {
  width: 180px;
  height: 36px;
  padding: 0 12px;
  border: 1px solid #c6daf7;
  border-radius: 4px;
  background: #fff;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #409eff;
    box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.12);
  }
}

.evidence-query-separator {
  color: #909399;
  font-size: 12px;
}

.steps-container {
  margin-bottom: 40px;
  padding: 30px 0;
  background: #fff;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.steps {
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: 600px;
  margin: 0 auto;
}

.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #999;

  &.active {
    color: #409eff;

    .step-number {
      background: #409eff;
      color: #fff;
    }
  }

  &.completed {
    color: #67c23a;

    .step-number {
      background: #67c23a;
    }
  }
}

.step-number {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #e4e7ed;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 18px;
  margin-bottom: 8px;
}

.step-label {
  font-size: 14px;
}

.step-line {
  flex: 1;
  height: 2px;
  background: #e4e7ed;
  margin: 0 20px;
  margin-bottom: 28px;

  &.active {
    background: #409eff;
  }
}

.step-content {
  background: #fff;
  border-radius: 4px;
  padding: 30px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.upload-section,
.preview-section,
.success-section {
  h3 {
    font-size: 20px;
    margin-bottom: 8px;
  }

  .desc {
    color: #666;
    margin-bottom: 30px;

    strong {
      color: #409eff;
    }
  }
}

.form-container {
  max-width: 600px;
}

.form-row {
  display: flex;
  align-items: center;
  margin-bottom: 20px;

  label {
    width: 120px;
    text-align: right;
    margin-right: 16px;
    font-weight: 500;

    .required {
      color: #f56c6c;
      margin-right: 4px;
    }
  }

  .form-input {
    flex: 1;
    height: 40px;
    padding: 0 12px;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    font-size: 14px;

    &:focus {
      outline: none;
      border-color: #409eff;
    }
  }
}

/* 业务类型切换按钮 */
.business-type-switch {
  display: flex;
  gap: 0;
  
  .type-btn {
    padding: 10px 24px;
    border: 1px solid #dcdfe6;
    background: #fff;
    color: #606266;
    cursor: pointer;
    font-size: 14px;
    transition: all 0.2s;
    
    &:first-child {
      border-radius: 4px 0 0 4px;
      border-right: none;
    }
    
    &:last-child {
      border-radius: 0 4px 4px 0;
    }
    
    &:hover:not(.active) {
      color: #409eff;
      border-color: #c6e2ff;
    }
    
    &.active {
      background: #409eff;
      color: #fff;
      border-color: #409eff;
    }
  }
}

.file-upload {
  flex: 1;

  input[type='file'] {
    display: none;
  }

  .file-label {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    background: #f5f7fa;
    border: 1px dashed #d9d9d9;
    border-radius: 4px;
    cursor: pointer;
    color: #606266;

    &:hover {
      border-color: #409eff;
      color: #409eff;
    }

    i {
      font-size: 16px;
    }
  }
}

.form-actions {
  margin-top: 30px;
  text-align: center;
}

.btn {
  padding: 12px 30px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;

  i {
    font-size: 16px;

    &.h-icon-loading {
      animation: rotate 1s linear infinite;
    }
  }

  &.btn-primary {
    background: #409eff;
    color: #fff;

    &:hover:not(:disabled) {
      background: #66b1ff;
    }

    &:disabled {
      background: #a0cfff;
      cursor: not-allowed;
    }
  }

  &.btn-success {
    background: #67c23a;
    color: #fff;

    &:hover:not(:disabled) {
      background: #85ce61;
    }

    &:disabled {
      background: #b3e19d;
      cursor: not-allowed;
    }
  }

  &.btn-default {
    background: #fff;
    color: #606266;
    border: 1px solid #dcdfe6;

    &:hover {
      border-color: #409eff;
      color: #409eff;
    }
  }
}

.btn-sm {
  padding: 7px 14px;
  font-size: 13px;
}

.btn.danger {
  color: #f56c6c;
  border-color: #fbc4c4;

  &:hover {
    background: #fef0f0;
    border-color: #f56c6c;
  }
}

.help-section {
  margin-top: 40px;
  padding: 20px;
  background: #f5f7fa;
  border-radius: 4px;

  h4 {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 16px;
    margin-bottom: 12px;
    color: #409eff;

    i {
      font-size: 18px;
    }
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 0;

    li {
      padding: 6px 0;
      color: #666;
      line-height: 1.6;
    }
  }
}

.preview-list {
  display: grid;
  gap: 20px;
  margin-bottom: 30px;
}

.voucher-card {
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  padding: 20px;
  background: #fafafa;
  transition: all 0.3s;

  &:hover {
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  }

  &.editing {
    border-color: #409eff;
    background: #fff;
    box-shadow: 0 2px 12px rgba(64, 158, 255, 0.2);
  }
}

.voucher-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;

  .voucher-title {
    display: flex;
    align-items: center;
    gap: 12px;

    strong {
      font-size: 16px;
      color: #303133;
    }

    .scenario-name {
      font-size: 13px;
      color: #909399;
      padding: 2px 8px;
      background: #f0f0f0;
      border-radius: 3px;
    }
  }

  .voucher-actions {
    display: flex;
    align-items: center;
    gap: 8px;

    .btn-icon {
      width: 32px;
      height: 32px;
      border: 1px solid #dcdfe6;
      background: #fff;
      border-radius: 4px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s;

      i {
        font-size: 16px;
        color: #606266;
      }

      &:hover {
        border-color: #409eff;
        i {
          color: #409eff;
        }
      }

      &.btn-success {
        border-color: #67c23a;
        &:hover {
          background: #67c23a;
          i {
            color: #fff;
          }
        }
      }

      &.btn-cancel {
        border-color: #f56c6c;
        &:hover {
          background: #f56c6c;
          i {
            color: #fff;
          }
        }
      }
    }
  }

  .voucher-date {
    color: #606266;
    font-size: 14px;
    margin-left: 8px;
  }
}

.voucher-summary {
  margin-bottom: 16px;
  padding: 10px 12px;
  background: #fff;
  border-left: 3px solid #409eff;
  color: #606266;
  font-size: 14px;

  label {
    font-weight: 500;
    margin-right: 8px;
  }

  .summary-input {
    flex: 1;
    min-width: 300px;
  }
}

.draft-meta-section {
  margin-bottom: 16px;
  padding: 14px 16px;
  background: #f8fbff;
  border: 1px solid #d9ecff;
  border-radius: 4px;
}

.evidence-chain-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 12px;
}

.draft-meta-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 20px;
  margin-bottom: 10px;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
}

.meta-label {
  color: #909399;
  min-width: 70px;
}

.meta-value {
  color: #303133;
  font-weight: 500;

  &.status-approved { color: #67c23a; }
  &.status-need_review { color: #e6a23c; }
  &.status-blocked { color: #f56c6c; }
}

.meta-text {
  font-size: 13px;
  color: #606266;
  margin-top: 8px;
  line-height: 1.6;

  &.feedback {
    color: #409eff;
  }
}

.meta-tags {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.tag-label {
  font-size: 12px;
  color: #909399;
}

.meta-tag {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 12px;

  &.risk {
    background: #fef0f0;
    color: #f56c6c;
  }

  &.hint {
    background: #fdf6ec;
    color: #e6a23c;
  }

  &.blocked {
    background: #fde2e2;
    color: #c45656;
  }
}

.reference-fact-section {
  margin-top: 12px;
  padding: 12px 14px;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 4px;

  &.empty {
    background: #f9fafb;
    border-color: #e5e7eb;
  }
}

.reference-fact-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;

  .rf-title {
    font-size: 14px;
    font-weight: 600;
    color: #303133;
  }
}

.rf-status {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 10px;
  font-size: 12px;

  &.rf-matched { background: #f0fdf4; color: #16a34a; }
  &.rf-partial,
  &.rf-candidate { background: #fdf6ec; color: #e6a23c; }
  &.rf-conflict { background: #fef0f0; color: #f56c6c; }
  &.rf-not_found,
  &.rf-no_match { background: #f9fafb; color: #9ca3af; }
}

.reference-fact-body {
  font-size: 13px;
}

.rf-facts {
  margin-bottom: 10px;
}

.rf-fact-item {
  padding: 8px 0;
  border-bottom: 1px dashed #e5e7eb;

  &:last-child {
    border-bottom: none;
  }
}

.rf-fact-row,
.rf-match-row {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 4px;
}

.rf-label {
  color: #909399;
  min-width: 72px;
  flex-shrink: 0;
  font-size: 12px;
}

.rf-value {
  color: #303133;
  font-weight: 500;
}

.rf-match {
  margin-bottom: 10px;
  padding: 10px;
  background: rgba(255, 255, 255, 0.6);
  border-radius: 4px;
}

.rf-match-header {
  font-size: 13px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 6px;
}

.rf-match-type {
  font-weight: 600;
  font-size: 12px;
  padding: 1px 8px;
  border-radius: 10px;

  &.match-exact { background: #dcfce7; color: #16a34a; }
  &.match-partial { background: #fef9c3; color: #a16207; }
  &.match-conflict { background: #fee2e2; color: #dc2626; }
  &.match-no_match { background: #f3f4f6; color: #6b7280; }
}

.rf-explanation {
  margin-bottom: 10px;
}

.rf-explanation-header {
  font-size: 13px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 6px;
}

.rf-explanation-summary {
  color: #4b5563;
  line-height: 1.6;
  margin-bottom: 6px;
}

.rf-explanation-highlights {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.rf-highlight-tag {
  display: inline-block;
  padding: 1px 8px;
  background: #e0f2fe;
  color: #0369a1;
  border-radius: 10px;
  font-size: 12px;
}

.rf-risks {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.evidence-chain-overlay,
.evidence-sample-overlay {
  position: fixed;
  inset: 0;
  z-index: 2100;
  background: rgba(15, 23, 42, 0.46);
  display: flex;
  justify-content: flex-end;
  align-items: stretch;
}

.evidence-sample-overlay {
  justify-content: center;
  align-items: center;
  z-index: 2200;
}

.evidence-chain-drawer {
  width: 92vw;
  max-width: 980px;
  height: 100%;
  background: #f8fbff;
  box-shadow: -8px 0 30px rgba(15, 23, 42, 0.2);
  display: flex;
  flex-direction: column;
}

.evidence-sample-dialog {
  width: 92vw;
  max-width: 760px;
  max-height: 86vh;
  overflow: auto;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.24);
  padding: 18px 22px 22px;
}

.evidence-chain-header,
.evidence-sample-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  padding: 18px 22px;
  border-bottom: 1px solid #e5eefb;
  background: #fff;

  h3 {
    margin: 0 0 6px;
    font-size: 20px;
    color: #1f2d3d;
  }

  p {
    margin: 0;
    color: #7a869a;
    font-size: 13px;
  }
}

.evidence-close-btn {
  width: 34px;
  height: 34px;
  border: 1px solid #dfe5ef;
  border-radius: 50%;
  background: #fff;
  color: #606266;
  cursor: pointer;

  &:hover {
    color: #409eff;
    border-color: #c6daf7;
  }
}

.evidence-chain-toolbar {
  padding: 14px 22px 0;
}

.evidence-chain-query-info {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.query-chip,
.sample-chip {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  background: #eaf3ff;
  color: #20528f;
  font-size: 12px;
}

.query-chip.muted {
  background: #f3f6fb;
  color: #606266;
}

.evidence-chain-body,
.evidence-sample-body {
  padding: 16px 22px 22px;
  overflow: auto;
  flex: 1;
}

.evidence-loading {
  padding: 40px 22px;
  text-align: center;
  color: #409eff;

  i {
    margin-right: 6px;
    animation: rotate 1s linear infinite;
  }
}

.evidence-empty-state {
  padding: 42px 24px;
  text-align: center;
  color: #8c97a8;
  background: #fff;
  border: 1px dashed #d9e4f2;
  border-radius: 8px;

  i {
    font-size: 34px;
    color: #c0c9d8;
  }

  .empty-title {
    margin-top: 12px;
    font-size: 16px;
    color: #344054;
    font-weight: 600;
  }

  .empty-desc {
    margin-top: 8px;
    font-size: 13px;
    line-height: 1.7;
  }

  &.error {
    border-color: #fbc4c4;
    background: #fff7f7;

    i {
      color: #f56c6c;
    }
  }
}

.evidence-stage-list {
  display: grid;
  gap: 14px;
}

.evidence-stage-card {
  border: 1px solid #dce7f5;
  border-radius: 8px;
  background: #fff;
  overflow: hidden;

  &.stage-post_failed {
    border-color: #fbc4c4;
    box-shadow: 0 0 0 1px rgba(245, 108, 108, 0.08);
  }
}

.stage-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 14px 16px;
  cursor: pointer;
  background: linear-gradient(180deg, #ffffff 0%, #fbfdff 100%);
}

.stage-card-title {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  color: #1f2d3d;
  font-weight: 600;
}

.stage-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  background: #eaf3ff;
  color: #20528f;
  font-size: 12px;
}

.stage-seq,
.stage-time {
  color: #6b7a90;
  font-size: 12px;
}

.stage-card-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 14px;
  justify-content: flex-end;
  color: #5b6575;
  font-size: 12px;
}

.stage-toggle-btn {
  padding: 6px 10px;
  border: 1px solid #dbe5f1;
  border-radius: 4px;
  background: #fff;
  color: #409eff;
  cursor: pointer;
  white-space: nowrap;
}

.stage-card-body {
  padding: 0 16px 16px;
}

.stage-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 16px;
  margin-top: 12px;
}

.stage-field {
  padding: 10px 12px;
  background: #f8fbff;
  border: 1px solid #e2edf9;
  border-radius: 6px;
}

.field-label {
  display: block;
  margin-bottom: 4px;
  color: #8a96a8;
  font-size: 12px;
}

.field-value {
  color: #1f2d3d;
  font-size: 13px;
  line-height: 1.6;
  word-break: break-word;
}

.stage-errors {
  margin-top: 12px;
}

.error-tags,
.sample-error-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.error-tag {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  background: #fff1f0;
  color: #cf1322;
  font-size: 12px;
  line-height: 1.4;
}

.stage-detail-actions {
  margin-top: 12px;
  display: flex;
  justify-content: flex-end;
}

.stage-detail-panels {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-top: 14px;
}

.detail-panel,
.sample-block {
  background: #fff;
  border: 1px solid #e2edf9;
  border-radius: 8px;
  padding: 12px;
}

.detail-title,
.sample-title {
  margin-bottom: 8px;
  color: #20528f;
  font-size: 13px;
  font-weight: 600;
}

.detail-panel pre,
.sample-block pre {
  margin: 0;
  max-height: 240px;
  overflow: auto;
  padding: 10px 12px;
  background: #0f172a;
  color: #dbeafe;
  border-radius: 6px;
  font-size: 12px;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
}

.sample-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 14px;
}

.failed-sample-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.failed-sample-card {
  background: #fff;
  border: 1px solid #e2edf9;
  border-radius: 8px;
  padding: 12px;
}

.sample-block + .sample-block {
  margin-top: 12px;
}

.sample-actions {
  display: flex;
  justify-content: flex-end;
}

.sample-text,
.sample-empty {
  color: #1f2d3d;
  font-size: 13px;
  line-height: 1.7;
}

.edit-tip {
  font-size: 12px;
  color: #909399;
  margin-left: 8px;
}
.manual-tag {
  display: inline-block;
  margin-left: 8px;
  padding: 2px 6px;
  border-radius: 10px;
  background: #fdf6ec;
  color: #e6a23c;
  font-size: 12px;
}
.confirmed-tag {
  display: inline-block;
  margin-left: 8px;
  padding: 2px 6px;
  border-radius: 10px;
  background: #f0f9eb;
  color: #67c23a;
  font-size: 12px;
}
.pending-tag {
  display: inline-block;
  margin-left: 8px;
  padding: 2px 6px;
  border-radius: 10px;
  background: #f4f4f5;
  color: #909399;
  font-size: 12px;
}
.unmatched-tip {
  font-size: 12px;
  color: #E6A23C;
  margin-left: 8px;
}
.status-tip {
  font-size: 13px;
  color: #606266;
  margin-right: 8px;
  strong {
    margin: 0 4px;
    &.pending { color: #e6a23c; }
    &.confirmed { color: #67c23a; }
    &.deleted { color: #f56c6c; }
  }
}

.edit-input {
  padding: 4px 8px;
  border: 1px solid #dcdfe6;
  border-radius: 3px;
  font-size: 13px;
  outline: none;

  &:focus {
    border-color: #409eff;
  }

  &.code-input {
    width: 80px;
    font-family: monospace;
    font-weight: 600;
    margin-right: 8px;
  }

  &.name-input {
    width: 200px;
  }

  &.amount-input {
    width: 100%;
    text-align: right;
    font-family: monospace;
  }

  &.summary-input {
    width: calc(100% - 50px);
    padding: 6px 10px;
  }
}

.voucher-table {
  width: 100%;
  border-collapse: collapse;
  background: #fff;

  thead {
    background: #f5f7fa;

    th {
      padding: 12px;
      text-align: left;
      font-weight: 600;
      color: #303133;
      border: 1px solid #ebeef5;
    }
  }

  tbody {
    tr:hover {
      background: #fafafa;
    }

    td {
      padding: 12px;
      border: 1px solid #ebeef5;
      color: #606266;

      &.text-right {
        text-align: right;
        font-family: monospace;
        font-weight: 500;
      }
    }
  }

  .subject-code {
    font-family: monospace;
    font-weight: 600;
    color: #409eff;
    margin-right: 8px;
  }

.subject-name {
    color: #606266;
  }
}

.entry-candidate-types,
.entry-line-reason,
.entry-auxiliary-readonly,
.entry-auxiliary-editor {
  margin-top: 6px;
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
}

.entry-candidate-types {
  color: #606266;
}

.aux-title {
  margin-bottom: 4px;
  color: #606266;
}

.aux-fields {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.aux-input,
.review-input {
  min-width: 140px;
}

.review-edit-section {
  margin-top: 16px;
  padding: 14px 16px;
  background: #fafafa;
  border: 1px solid #ebeef5;
  border-radius: 4px;
}

.review-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;

  &:last-child {
    margin-bottom: 0;
  }

  label {
    width: 72px;
    font-size: 13px;
    color: #606266;
  }
}

.action-cell {
  text-align: center;
  padding: 4px !important;
}

.btn-row-del {
  width: 22px;
  height: 22px;
  line-height: 20px;
  border: 1px solid #f56c6c;
  background: #fff;
  color: #f56c6c;
  border-radius: 50%;
  cursor: pointer;
  font-size: 14px;
  padding: 0;

  &:hover {
    background: #f56c6c;
    color: #fff;
  }
}

.add-row-tr td {
  padding: 8px 12px !important;
  border: none !important;
  background: #fafafa;
}

.btn-add-row {
  padding: 4px 14px;
  border: 1px dashed #409eff;
  background: transparent;
  color: #409eff;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;

  &:hover {
    background: #ecf5ff;
  }

  i { margin-right: 4px; }
}

.subject-edit-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.subject-search-box {
  position: relative;
}

.subject-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 999;
  background: #fff;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  min-width: 240px;
  max-height: 200px;
  overflow-y: auto;
  box-shadow: 0 2px 8px rgba(0,0,0,0.12);
}

.subject-option {
  padding: 6px 12px;
  font-size: 13px;
  cursor: pointer;
  color: #303133;

  &:hover {
    background: #f5f7fa;
    color: #409eff;
  }
}

.currency-sub-row td {
  background: #f8f9ff;
  padding: 6px 12px !important;
}

.foreign-fields {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.currency-select {
  height: 30px;
  padding: 0 8px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  font-size: 13px;
  min-width: 130px;

  &:focus { outline: none; border-color: #409eff; }
}

.foreign-input {
  width: 100px !important;
}

.btn-get-rate {
  padding: 4px 10px;
  height: 30px;
  border: 1px solid #409eff;
  background: #fff;
  color: #409eff;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  white-space: nowrap;

  &:hover:not(:disabled) { background: #409eff; color: #fff; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
}

.balance-row {
  font-weight: 600;
  background: #f0f9ff;

  &.unbalanced {
    background: #fef0f0;
    color: #f56c6c;

    td {
      border-color: #f56c6c;
    }
  }

  td {
    padding: 12px;
    border: 2px solid #409eff;
  }
}

.preview-actions {
  display: flex;
  justify-content: center;
  gap: 20px;
}

.preview-stats {
  margin-bottom: 20px;
  padding: 16px;
  background: #f5f7fa;
  border-radius: 4px;
  text-align: center;

  .stats-bar {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 24px;
    font-size: 14px;
    color: #606266;

    strong {
      font-size: 18px;
      margin: 0 4px;
    }

    .stat-item {
      &.pending { color: #e6a23c; }
      &.confirmed { color: #67c23a; }
      &.deleted { color: #f56c6c; }
    }
  }

  .process-tip {
    margin-top: 10px;
    font-size: 13px;
    color: #e6a23c;

    i {
      margin-right: 4px;
    }
  }
}

.success-section {
  text-align: center;
  padding: 40px 0;

  .success-icon {
    margin-bottom: 20px;

    i {
      font-size: 80px;
      color: #67c23a;
    }
  }

  h3 {
    font-size: 24px;
    color: #303133;
    margin-bottom: 12px;
  }

  .desc {
    font-size: 16px;
    margin-bottom: 40px;
  }

  .success-actions {
    display: flex;
    justify-content: center;
    gap: 20px;
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Memory Review Diff Visualization */
.memory-diff-section {
  margin: 12px 0;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  overflow: hidden;
}

.diff-header {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: #fffbe6;
  border-bottom: 1px solid #ffe58f;
  font-weight: 600;
  font-size: 13px;
  color: #d48806;
}

.diff-details {
  border-bottom: 1px solid #f0f0f0;
}

.diff-details summary {
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 600;
  color: #555;
  cursor: pointer;
  background: #fafafa;
}

.diff-details summary:hover {
  background: #f0f0f0;
}

.diff-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.diff-table th {
  background: #fafafa;
  padding: 6px 8px;
  text-align: left;
  border-bottom: 1px solid #f0f0f0;
  font-weight: 600;
  color: #333;
}

.diff-table td {
  padding: 6px 8px;
  border-bottom: 1px solid #f5f5f5;
}

.diff-row:hover td {
  background: #fafafa;
}

.diff-row.suggested td {
  background: #f0f9ff;
}

.diff-row.reviewed td {
  background: #f6ffed;
}

.diff-old {
  background: #fff2f0;
  color: #ff4d4f;
  text-decoration: line-through;
  font-family: 'SF Mono', 'Menlo', monospace;
  font-size: 11px;
}

.diff-new {
  background: #f6ffed;
  color: #52c41a;
  font-weight: 600;
  font-family: 'SF Mono', 'Menlo', monospace;
  font-size: 11px;
}

.diff-summary {
  border-top: 1px solid #ffe58f;
}

.diff-subheader {
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 600;
  color: #d48806;
  background: #fffbe6;
}

.diff-field-table td {
  font-family: 'SF Mono', 'Menlo', monospace;
  font-size: 11px;
}

.diff-no-change {
  padding: 20px;
  text-align: center;
  color: #52c41a;
  font-size: 13px;
  font-weight: 600;
  background: #f6ffed;
}

.diff-no-change i {
  margin-right: 4px;
}

.llm-draft-section {
  margin: 12px 0;
  padding: 12px;
  border: 1px solid #d9f7be;
  border-radius: 8px;
  background: #f6ffed;
}

.llm-draft-section.llm-section-warning {
  border-color: #ffd666;
  background: #fffbe6;
}

.llm-draft-section.llm-section-danger {
  border-color: #ffccc7;
  background: #fff2f0;
}

.llm-draft-section.llm-section-default {
  border-color: #d9d9d9;
  background: #fafafa;
}

.llm-draft-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: #237804;
  margin-bottom: 8px;
}

.llm-draft-section.llm-section-warning .llm-draft-header { color: #d48806; }
.llm-draft-section.llm-section-danger .llm-draft-header { color: #cf1322; }
.llm-draft-section.llm-section-default .llm-draft-header { color: #595959; }

.llm-draft-status-row {
  margin-bottom: 10px;
}

.llm-status-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 600;
}

.llm-badge-success {
  background: #f6ffed;
  border: 1px solid #52c41a;
  color: #237804;
}

.llm-badge-warning {
  background: #fffbe6;
  border: 1px solid #faad14;
  color: #d48806;
}

.llm-badge-danger {
  background: #fff2f0;
  border: 1px solid #ff4d4f;
  color: #cf1322;
}

.llm-badge-default {
  background: #f5f5f5;
  border: 1px solid #d9d9d9;
  color: #8c8c8c;
}

.llm-risk-section {
  margin-bottom: 10px;
  padding: 8px 10px;
  background: #fff7e6;
  border-left: 3px solid #fa8c16;
  border-radius: 4px;
}

.llm-risk-label {
  font-weight: 600;
  color: #d46b08;
  margin-bottom: 4px;
  font-size: 13px;
}

.llm-risk-list {
  margin: 0;
  padding-left: 18px;
}

.llm-risk-item {
  color: #d46b08;
  font-size: 13px;
  line-height: 1.6;
}

.llm-skipped-reason {
  margin: 6px 0 10px;
  padding: 6px 10px;
  background: #f5f5f5;
  border-radius: 4px;
  color: #595959;
  font-size: 13px;
}

.llm-validation-errors {
  margin: 8px 0;
  padding: 8px 10px;
  background: #fff2f0;
  border-left: 3px solid #ff4d4f;
  border-radius: 4px;
}

.llm-val-title {
  font-weight: 600;
  color: #cf1322;
  font-size: 13px;
  margin-bottom: 2px;
}

.llm-val-text {
  color: #cf1322;
  font-size: 13px;
}

.llm-draft-lines-header {
  font-weight: 600;
  color: #333;
  margin: 10px 0 4px;
  font-size: 13px;
}

.llm-no-lines {
  padding: 8px;
  background: #f5f5f5;
  color: #8c8c8c;
  font-size: 13px;
  border-radius: 4px;
}

.llm-draft-tech-details {
  margin-top: 10px;
  border-top: 1px solid #e8e8e8;
  padding-top: 8px;
}

.llm-draft-tech-details summary {
  font-size: 12px;
  color: #8c8c8c;
  cursor: pointer;
}

.llm-draft-disclaimer {
  margin-top: 10px;
  padding: 6px 10px;
  background: #e6f7ff;
  border-radius: 4px;
  font-size: 12px;
  color: #1890ff;
  border: 1px solid #91d5ff;
}

.llm-draft-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.llm-draft-text {
  margin-bottom: 8px;
  color: #333;
  line-height: 1.6;
}

.llm-draft-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  background: #fff;
}

.llm-draft-table th,
.llm-draft-table td {
  padding: 8px;
  border-bottom: 1px solid #f0f0f0;
  text-align: left;
}

.llm-draft-table th {
  background: #fafafa;
}

.meta-chip {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  background: #fff;
  border: 1px solid #b7eb8f;
  color: #389e0d;
}
</style>
